import { ActivityFeed } from "@/components/ActivityFeed";
import { EntityChip } from "@/components/EntityChip";
import { EntityFiles } from "@/components/EntityFiles";
import { ImportDialog } from "@/components/ImportDialog";
import {
	type FieldDef,
	PartyFieldRenderer,
} from "@/components/PartyFieldRenderer";
import { PartyLink } from "@/components/PartyLink";
import { CommerceStatusBadge } from "@/components/commerce/commerce-status-badge";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect.js";
import type { PricedProduct } from "@/gen/omnios/v1/catalog_pb.js";
import { ExportTemplateService } from "@/gen/omnios/v1/export_template_connect.js";
import { MemberService } from "@/gen/omnios/v1/member_connect.js";
import { OrderService } from "@/gen/omnios/v1/order_connect.js";
import { PartyService } from "@/gen/omnios/v1/party_connect.js";
import {
	type ChannelKind,
	type ChannelScope,
	PartyKind,
} from "@/gen/omnios/v1/party_pb.js";
import type { PartyPreferences } from "@/gen/omnios/v1/party_pb.js";
import { QuoteService } from "@/gen/omnios/v1/quote_connect.js";
import { QuoteSourceType } from "@/gen/omnios/v1/quote_pb.js";
import { StoreService } from "@/gen/omnios/v1/store_connect.js";
import { WorkItemService } from "@/gen/omnios/v1/work_item_connect.js";
import { CURRENCIES } from "@/lib/currencies";
import { formatCurrency, formatDate } from "@/lib/format";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { Struct } from "@bufbuild/protobuf";
import { createClient } from "@connectrpc/connect";
import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	ArrowDown,
	ArrowUp,
	ArrowUpDown,
	Building2,
	Check,
	CheckSquare,
	Clock,
	Copy,
	CreditCard,
	Edit2,
	FileSpreadsheet,
	FileText,
	Link2,
	Loader2,
	Mail,
	MoreHorizontal,
	Package,
	Percent,
	Phone,
	Plus,
	Search,
	Settings,
	ShoppingCart,
	Star,
	Trash2,
	Upload,
	User,
	UserPlus,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/parties/$id")({
	component: PartyDetailPage,
});

const partyClient = createClient(PartyService, transport);
const workItemClient = createClient(WorkItemService, transport);
const memberClient = createClient(MemberService, transport);
const orderClient = createClient(OrderService, transport);
const exportClient = createClient(ExportTemplateService, transport);
const catalogClient = createClient(CatalogService, transport);
const quoteClient = createClient(QuoteService, transport);

const CHANNEL_KIND_LABELS: Record<number, string> = {
	1: "WhatsApp",
	2: "Telegram",
	3: "Email",
	4: "SMS",
};

function PartyDetailPage() {
	const { t } = useTranslation("parties");
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const invalidatePartyCollections = useCallback(() => {
		qc.invalidateQueries({ queryKey: ["parties"] });
		qc.invalidateQueries({ queryKey: ["parties", "overview"] });
		qc.invalidateQueries({ queryKey: ["stats", "parties"] });
	}, [qc]);

	const {
		data: party,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["party", id],
		queryFn: () => partyClient.getParty({ id }),
	});

	const deleteMut = useMutation({
		mutationFn: () => partyClient.deleteParty({ id }),
		onSuccess: () => {
			invalidatePartyCollections();
			qc.invalidateQueries({ queryKey: ["entity-activity"] });
			qc.invalidateQueries({ queryKey: ["global-activity"] });
			toast.success(t("delete_success"));
			void navigate({ to: "/parties" });
		},
		onError: () => toast.error(t("delete_error")),
	});

	useBreadcrumbs([
		{ label: t("breadcrumb"), href: "/parties" },
		{ label: party?.displayName || party?.name || "…" },
	]);

	if (isLoading) return <PageSkeleton />;

	if (error || !party) {
		return (
			<div className="flex items-center justify-center h-[60vh]">
				<div className="text-center">
					<h3 className="text-lg font-semibold mb-1">
						{t("error_loading_title")}
					</h3>
					<p className="text-sm text-muted-foreground">
						{error?.message ?? t("error_not_found")}
					</p>
					<Link
						to="/parties"
						className="mt-4 inline-block text-sm text-primary underline"
					>
						{t("back_to_contacts")}
					</Link>
				</div>
			</div>
		);
	}

	const KIND_LABELS: Record<number, string> = {
		1: t("kind_person"),
		2: t("kind_organization"),
		3: t("kind_team"),
		4: t("kind_agent"),
	};
	const kindStr = KIND_LABELS[party.kind as number] ?? t("kind_person");
	const attrs = party.attrs?.fields
		? Object.fromEntries(
				Object.entries(party.attrs.fields as Record<string, any>).map(
					([k, v]) => [
						k,
						v?.stringValue ?? v?.numberValue ?? v?.boolValue ?? "",
					],
				),
			)
		: {};

	return (
		<PartyEditor
			party={party}
			kindStr={kindStr}
			attrs={attrs}
			onDelete={() => deleteMut.mutate()}
			isDeleting={deleteMut.isPending}
		/>
	);
}

function PageSkeleton() {
	return (
		<div className="flex flex-col gap-4 max-w-5xl mx-auto px-4 py-4 w-full overflow-auto h-full">
			<Card className="space-y-4 px-4 py-3">
				<Skeleton className="h-10 w-2/3" />
				<Skeleton className="h-6 w-1/3" />
			</Card>
			<Skeleton className="h-9 w-80 rounded" />
			<Card className="space-y-4 px-4 py-3">
				<Skeleton className="h-32 w-full rounded" />
			</Card>
		</div>
	);
}

function PartyEditor({
	party,
	kindStr,
	attrs,
	onDelete,
	isDeleting,
}: {
	party: any;
	kindStr: string;
	attrs: Record<string, string>;
	onDelete: () => void;
	isDeleting: boolean;
}) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const navigate = useNavigate();
	const [editDisplayName, setEditDisplayName] = useState(party.displayName);
	const [channelDialogOpen, setChannelDialogOpen] = useState(false);
	const [linkContactDialogOpen, setLinkContactDialogOpen] = useState(false);
	const [linkOwnerDialogOpen, setLinkOwnerDialogOpen] = useState(false);

	const [idCopied, setIdCopied] = useState(false);
	const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
	const copyId = useCallback(() => {
		navigator.clipboard.writeText(party.id);
		setIdCopied(true);
		setTimeout(() => setIdCopied(false), 1500);
	}, [party.id]);

	const invalidateParty = useCallback(() => {
		qc.invalidateQueries({ queryKey: ["party", party.id] });
	}, [qc, party.id]);

	const invalidatePartyCollections = useCallback(() => {
		qc.invalidateQueries({ queryKey: ["parties"] });
		qc.invalidateQueries({ queryKey: ["parties", "overview"] });
		qc.invalidateQueries({ queryKey: ["stats", "parties"] });
	}, [qc]);

	const invalidateActivity = useCallback(() => {
		qc.invalidateQueries({ queryKey: ["entity-activity", "party", party.id] });
		qc.invalidateQueries({ queryKey: ["global-activity"] });
	}, [qc, party.id]);

	const handlePartyListsChanged = useCallback(() => {
		invalidateParty();
		invalidatePartyCollections();
	}, [invalidateParty, invalidatePartyCollections]);

	const updateMut = useMutation({
		mutationFn: () =>
			partyClient.updateParty({
				id: party.id,
				displayName: editDisplayName,
			}),
		onSuccess: () => {
			handlePartyListsChanged();
			invalidateActivity();
		},
		onError: () => toast.error(t("save_failed")),
	});

	const createOrderMut = useMutation({
		mutationFn: () =>
			orderClient.createOrder({
				title: t("draft_order_for_party", {
					name: party.displayName || party.name,
				}),
				description: "",
				isDraft: true,
				customerPartyId: party.id,
			}),
		onSuccess: (order) => {
			qc.invalidateQueries({ queryKey: ["orders"] });
			qc.invalidateQueries({ queryKey: ["order-analytics"] });
			qc.invalidateQueries({ queryKey: ["home", "orders"] });
			qc.invalidateQueries({ queryKey: ["stats", "orders"] });
			qc.invalidateQueries({ queryKey: ["customer-order-stats", party.id] });
			toast.success(t("create_order_success"));
			void navigate({ to: "/commerce/orders/$id", params: { id: order.id } });
		},
		onError: () => toast.error(t("create_order_error")),
	});

	const createQuoteMut = useMutation({
		mutationFn: () =>
			quoteClient.createQuote({
				title: t("draft_quote_for_party", {
					name: party.displayName || party.name,
				}),
				customerPartyId: party.id,
				currency: "RUB",
				validityDays: 14,
				notes: "",
				sourceType: QuoteSourceType.MANUAL,
			}),
		onSuccess: (quote) => {
			qc.invalidateQueries({ queryKey: ["quotes"] });
			toast.success(t("create_quote_success"));
			void navigate({ to: "/commerce/quotes/$id", params: { id: quote.id } });
		},
		onError: () => toast.error(t("create_quote_error")),
	});

	const [importOpen, setImportOpen] = useState(false);

	const isOrg = party.kind === 2;
	const isPerson = party.kind === 1;
	const roles = party.roles ?? [];
	const hasRole = (r: string) => roles.includes(r);

	const isCustomer = hasRole("customer");
	const isSupplier = hasRole("supplier");
	const showWorkItems = !hasRole("competitor");
	const defaultTab = isCustomer
		? "client"
		: isSupplier
			? "supplier"
			: "overview";

	// Split relationships into contact links vs owner/manager assignments
	const rels = party.relationships ?? [];
	const contactRels = rels.filter(
		(r: any) => r.kind === "contacts_for" || r.kind === "employs",
	);
	const ownerRels = rels.filter(
		(r: any) => r.kind === "manages" || r.kind === "owns",
	);

	const emailChannel = (party.channels ?? []).find((ch: any) => ch.kind === 3);

	const contactsTab = (
		<div className="space-y-4">
			{/* Channels */}
			<Card className="px-4 py-3 gap-3">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium flex items-center gap-1.5">
						<Mail className="h-4 w-4 text-muted-foreground" />
						{t("channels_label")}
					</h3>
					<Button
						size="sm"
						variant="ghost"
						className="h-7 text-xs"
						onClick={() => setChannelDialogOpen(true)}
					>
						<Plus className="h-3 w-3 mr-1" />
						{t("channel_more")}
					</Button>
				</div>
				<InlineChannelAdd
					partyId={party.id}
					onAdded={() =>
						qc.invalidateQueries({ queryKey: ["party", party.id] })
					}
				/>
				{!party.channels || party.channels.length === 0 ? (
					<p className="text-sm text-muted-foreground">{t("no_channels")}</p>
				) : (
					<div className="space-y-1.5">
						{party.channels.map((ch: any) => (
							<div key={ch.id} className="flex items-center gap-2 text-sm">
								{ch.kind === 3 ? (
									<Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
								) : (
									<Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
								)}
								<span className="truncate">{ch.identifier}</span>
								<Badge variant="outline" className="text-[10px] shrink-0">
									{CHANNEL_KIND_LABELS[ch.kind] ??
										(ch.kind === 5 ? t("channel_phone") : t("channel_other"))}
								</Badge>
							</div>
						))}
					</div>
				)}
			</Card>

			{/* Contact persons / org link */}
			<Card className="px-4 py-3 gap-3">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium flex items-center gap-1.5">
						<UserPlus className="h-4 w-4 text-muted-foreground" />
						{isOrg
							? t("contact_persons")
							: isPerson
								? t("organization_label")
								: t("links_label")}
					</h3>
					<Button
						size="sm"
						variant="ghost"
						className="h-7 text-xs"
						onClick={() => setLinkContactDialogOpen(true)}
					>
						<Link2 className="h-3 w-3 mr-1" />
						{t("link_action")}
					</Button>
				</div>
				{contactRels.length === 0 ? (
					<p className="text-xs text-muted-foreground">
						{isOrg
							? t("no_contact_persons")
							: isPerson
								? t("not_linked_to_org")
								: t("no_links")}
					</p>
				) : (
					<div className="space-y-1.5">
						{contactRels.map((rel: any) => {
							const otherId =
								rel.fromPartyId === party.id ? rel.toPartyId : rel.fromPartyId;
							return (
								<div key={rel.id} className="flex items-center gap-1.5 text-sm">
									<PartyLink partyId={otherId} />
									{rel.role && (
										<Badge variant="outline" className="text-[10px] shrink-0">
											{rel.role}
										</Badge>
									)}
								</div>
							);
						})}
					</div>
				)}
			</Card>
		</div>
	);

	const teamTab = (
		<div className="space-y-4">
			<SalesRepCard
				partyId={party.id}
				currentSalesRepUserId={party.salesRepUserId ?? ""}
				onChanged={invalidateParty}
			/>
			<Card className="px-4 py-3 gap-3">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium flex items-center gap-1.5">
						<User className="h-4 w-4 text-muted-foreground" />
						{t("responsible_label")}
					</h3>
					<Button
						size="sm"
						variant="ghost"
						className="h-7 text-xs"
						onClick={() => setLinkOwnerDialogOpen(true)}
					>
						<UserPlus className="h-3 w-3 mr-1" />
						{t("assign_action")}
					</Button>
				</div>
				{ownerRels.length === 0 ? (
					<p className="text-xs text-muted-foreground">{t("no_responsible")}</p>
				) : (
					<div className="space-y-1.5">
						{ownerRels.map((rel: any) => {
							const otherId =
								rel.fromPartyId === party.id ? rel.toPartyId : rel.fromPartyId;
							return (
								<div key={rel.id} className="flex items-center gap-1.5 text-sm">
									<PartyLink partyId={otherId} />
									{rel.role && (
										<Badge variant="outline" className="text-[10px] shrink-0">
											{rel.role}
										</Badge>
									)}
								</div>
							);
						})}
					</div>
				)}
			</Card>
		</div>
	);

	const overviewTab = (
		<div className="space-y-4">
			<PartyCustomFields
				partyId={party.id}
				partyKind={party.kind}
				attrs={attrs}
				onSaved={invalidateParty}
			/>
			<EntityFiles entityType="party" entityId={party.id} />
			<Card className="px-4 py-3 gap-3">
				<h3 className="text-sm font-medium flex items-center gap-1.5">
					<Clock className="h-4 w-4 text-muted-foreground" />
					{t("timeline")}
				</h3>
				<PartyTimeline partyId={party.id} />
			</Card>
			<LinkedRecordsCard
				partyId={party.id}
				showOrders={isCustomer || isSupplier}
				showWorkItems={showWorkItems}
			/>
		</div>
	);

	const clientTab = isCustomer ? (
		<div className="space-y-4">
			<GenerateClientExportCard partyId={party.id} />
			<CustomerMarginCard partyId={party.id} />
			<CommercialInfoCard
				party={party}
				onSaved={invalidateParty}
				mode="sales"
			/>
			<PartyAvailableProducts partyId={party.id} />
			<Card className="px-4 py-3 gap-3">
				<h3 className="text-sm font-medium flex items-center gap-1.5">
					<Settings className="h-4 w-4 text-muted-foreground" />
					{t("client_settings")}
				</h3>
				<PartyPreferencesPanel partyId={party.id} />
			</Card>
			<CustomerStatsCard partyId={party.id} />
			<Card className="px-4 py-3 gap-2">
				<Link
					to="/accounting/counterparty-recon"
					className="text-sm text-primary hover:underline flex items-center gap-1.5"
				>
					<FileSpreadsheet className="h-4 w-4" />
					{t("open_recon_customer")}
				</Link>
			</Card>
		</div>
	) : null;

	const supplierTab = isSupplier ? (
		<div className="space-y-4">
			<SupplierFeedsCard partyId={party.id} />

			<PartyOwnCatalog partyId={party.id} />
			<CommercialInfoCard
				party={party}
				onSaved={invalidateParty}
				mode="procurement"
			/>
			<Card className="px-4 py-3 gap-2">
				<h3 className="text-sm font-medium flex items-center gap-1.5">
					<Package className="h-4 w-4 text-muted-foreground" />
					{t("purchase_history_title")}
				</h3>
				<p className="text-xs text-muted-foreground">
					{t("purchase_history_empty")}
				</p>
			</Card>
			<Card className="px-4 py-3 gap-2">
				<Link
					to="/accounting/counterparty-recon"
					className="text-sm text-primary hover:underline flex items-center gap-1.5"
				>
					<FileSpreadsheet className="h-4 w-4" />
					{t("open_recon_supplier")}
				</Link>
			</Card>
		</div>
	) : null;

	return (
		<>
			<div className="flex flex-col gap-4 max-w-5xl mx-auto px-4 py-4 w-full overflow-auto h-full">
				{/* Header */}
				<Card className="px-4 py-3 gap-3">
					<div className="flex items-start gap-3">
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
							{isOrg ? (
								<Building2 className="h-5 w-5" />
							) : (
								<User className="h-5 w-5" />
							)}
						</div>
						<div className="flex-1 min-w-0 space-y-1">
							<Input
								value={editDisplayName || party.name}
								onChange={(e) => {
									setEditDisplayName(e.target.value);
									updateMut.reset();
								}}
								onBlur={() => {
									if (editDisplayName !== party.displayName) updateMut.mutate();
								}}
								className="text-xl font-semibold border-none shadow-none px-1 -mx-1 h-auto focus-visible:ring-0 rounded hover:bg-muted/50 focus:bg-muted/50 transition-colors"
								placeholder={t("placeholder_display_name")}
							/>
							<div className="flex items-center gap-2 px-1 flex-wrap">
								<Badge variant="secondary">{kindStr}</Badge>
								{party.isInternal && (
									<Badge variant="outline">{t("internal_badge")}</Badge>
								)}
								<RoleBadges
									partyId={party.id}
									roles={roles}
									onChanged={handlePartyListsChanged}
								/>
								{updateMut.isPending && (
									<span className="text-[11px] text-muted-foreground flex items-center gap-1">
										<Loader2 className="h-3 w-3 animate-spin" />
									</span>
								)}
								{updateMut.isSuccess && (
									<span className="text-[11px] text-muted-foreground flex items-center gap-1">
										<Check className="h-3 w-3" />
										{t("saved")}
									</span>
								)}
							</div>
						</div>
						<div className="flex items-center gap-1.5 shrink-0">
							{isCustomer && (
								<Button
									size="sm"
									className="h-8 text-xs"
									onClick={() => createQuoteMut.mutate()}
									disabled={createQuoteMut.isPending}
								>
									{createQuoteMut.isPending ? (
										<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
									) : (
										<FileText className="h-3.5 w-3.5 mr-1.5" />
									)}
									{t("action_create_quote")}
								</Button>
							)}
							{isSupplier && (
								<Button
									size="sm"
									variant={isCustomer ? "outline" : "default"}
									className="h-8 text-xs"
									onClick={() => setImportOpen(true)}
								>
									<Upload className="h-3.5 w-3.5 mr-1.5" />
									{t("action_import_catalog")}
								</Button>
							)}
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="ghost" size="icon" className="h-8 w-8">
										<MoreHorizontal className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="end">
									{(isCustomer || isSupplier) && (
										<DropdownMenuItem
											onClick={() => createOrderMut.mutate()}
											disabled={createOrderMut.isPending}
										>
											<ShoppingCart className="h-3.5 w-3.5 mr-2" />
											{t("action_create_order")}
										</DropdownMenuItem>
									)}
									{emailChannel && (
										<DropdownMenuItem
											onClick={() =>
												window.open(`mailto:${emailChannel.identifier}`)
											}
										>
											<Mail className="h-3.5 w-3.5 mr-2" />
											{t("action_send_email")}
										</DropdownMenuItem>
									)}
									{isCustomer && (
										<DropdownMenuItem onClick={() => setInviteDialogOpen(true)}>
											<UserPlus className="h-3.5 w-3.5 mr-2" />
											{t("action_connect_client")}
										</DropdownMenuItem>
									)}
									<DropdownMenuItem onClick={copyId}>
										{idCopied ? (
											<Check className="h-3.5 w-3.5 mr-2 text-green-600" />
										) : (
											<Copy className="h-3.5 w-3.5 mr-2" />
										)}
										{idCopied ? t("common:copied") : t("copy_id")}
									</DropdownMenuItem>
									<AlertDialog>
										<AlertDialogTrigger asChild>
											<DropdownMenuItem
												onSelect={(e) => e.preventDefault()}
												disabled={isDeleting}
												className="text-destructive focus:text-destructive"
											>
												<Trash2 className="h-3.5 w-3.5 mr-2" />
												{t("delete_contact")}
											</DropdownMenuItem>
										</AlertDialogTrigger>
										<AlertDialogContent>
											<AlertDialogHeader>
												<AlertDialogTitle>
													{t("delete_contact_confirm")}
												</AlertDialogTitle>
												<AlertDialogDescription>
													{t("delete_contact_description", {
														name: party.displayName || party.name,
													})}
												</AlertDialogDescription>
											</AlertDialogHeader>
											<AlertDialogFooter>
												<AlertDialogCancel>
													{t("common:cancel")}
												</AlertDialogCancel>
												<AlertDialogAction
													onClick={onDelete}
													className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
												>
													{t("common:delete")}
												</AlertDialogAction>
											</AlertDialogFooter>
										</AlertDialogContent>
									</AlertDialog>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					</div>
					{Number(party.relationshipScore) > 0 && (
						<div className="flex items-center gap-2 pt-1">
							<span className="text-xs text-muted-foreground">
								{t("relationship_score")}
							</span>
							<div className="h-1.5 flex-1 max-w-[160px] rounded-full bg-muted overflow-hidden">
								<div
									className="h-full rounded-full bg-primary transition-all"
									style={{
										width: `${Math.round(Number(party.relationshipScore) * 100)}%`,
									}}
								/>
							</div>
							<span className="text-xs font-medium tabular-nums">
								{Math.round(Number(party.relationshipScore) * 100)}%
							</span>
						</div>
					)}
					<div className="text-[10px] text-muted-foreground/40 font-mono break-all select-all">
						{party.id}
					</div>
				</Card>

				{/* Tabs */}
				<Tabs defaultValue={defaultTab} className="w-full">
					<TabsList className="h-auto p-0.5 bg-muted/50 flex-wrap">
						<TabsTrigger value="overview" className="text-xs">
							{t("tab_overview")}
						</TabsTrigger>
						<TabsTrigger value="contacts" className="text-xs">
							{t("tab_contacts")}
						</TabsTrigger>
						<TabsTrigger value="team" className="text-xs">
							{t("tab_team")}
						</TabsTrigger>
						{isCustomer && (
							<TabsTrigger value="client" className="text-xs">
								{t("tab_client")}
							</TabsTrigger>
						)}
						{isCustomer && (
							<TabsTrigger value="quotes" className="text-xs">
								{t("tab_quotes")}
							</TabsTrigger>
						)}
						{isSupplier && (
							<TabsTrigger value="supplier" className="text-xs">
								{t("tab_supplier")}
							</TabsTrigger>
						)}
					</TabsList>
					<TabsContent value="overview" className="mt-4">
						{overviewTab}
					</TabsContent>
					<TabsContent value="contacts" className="mt-4">
						{contactsTab}
					</TabsContent>
					<TabsContent value="team" className="mt-4">
						{teamTab}
					</TabsContent>
					{isCustomer && (
						<TabsContent value="client" className="mt-4">
							{clientTab}
						</TabsContent>
					)}
					{isCustomer && (
						<TabsContent value="quotes" className="mt-4">
							<PartyQuotesTab partyId={party.id} />
						</TabsContent>
					)}
					{isSupplier && (
						<TabsContent value="supplier" className="mt-4">
							{supplierTab}
						</TabsContent>
					)}
				</Tabs>
			</div>

			<AddChannelDialog
				open={channelDialogOpen}
				onOpenChange={setChannelDialogOpen}
				partyId={party.id}
				onCreated={() => {
					qc.invalidateQueries({ queryKey: ["party", party.id] });
					invalidateActivity();
				}}
			/>
			<LinkPartyDialog
				open={linkContactDialogOpen}
				onOpenChange={setLinkContactDialogOpen}
				partyId={party.id}
				partyKind={party.kind}
				linkKind="contact"
				onLinked={() => {
					qc.invalidateQueries({ queryKey: ["party", party.id] });
					invalidateActivity();
				}}
			/>
			<LinkPartyDialog
				open={linkOwnerDialogOpen}
				onOpenChange={setLinkOwnerDialogOpen}
				partyId={party.id}
				partyKind={party.kind}
				linkKind="owner"
				onLinked={() => {
					qc.invalidateQueries({ queryKey: ["party", party.id] });
					invalidateActivity();
				}}
			/>
			<ClientInviteDialog
				open={inviteDialogOpen}
				onOpenChange={setInviteDialogOpen}
				partyId={party.id}
				defaultEmail={emailChannel?.identifier ?? ""}
			/>
			<ImportDialog
				open={importOpen}
				onOpenChange={setImportOpen}
				defaultDestination="supplier"
				defaultSupplierId={party.id}
			/>
		</>
	);
}

/* ── Client Invite Dialog ── */

function ClientInviteDialog({
	open,
	onOpenChange,
	partyId,
	defaultEmail,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	partyId: string;
	defaultEmail: string;
}) {
	const { t } = useTranslation("parties");
	const [email, setEmail] = useState(defaultEmail);
	const [redirectUrl, setRedirectUrl] = useState("");
	const [portalInstanceId, setPortalInstanceId] = useState<string>("");
	const [portalType, setPortalType] = useState<string>("customer");
	const [sending, setSending] = useState(false);

	useEffect(() => {
		if (open) setEmail(defaultEmail);
	}, [open, defaultEmail]);

	const qc = useQueryClient();
	const partyClient = createClient(PartyService, transport);
	const storeClient = createClient(StoreService, transport);

	const { data: invites } = useQuery({
		queryKey: ["client-invites", partyId],
		queryFn: () => partyClient.listClientInvites({ partyId }),
		enabled: open,
	});

	const { data: portalsResp } = useQuery({
		queryKey: ["portal-instances"],
		queryFn: () => storeClient.listPortalInstances({}),
		enabled: open,
	});

	// Filter portal instances by selected portal_type so the dropdown only
	// shows matching tenants.
	const portals = (portalsResp?.portalInstances ?? []).filter((p) =>
		portalType ? p.portalTypeId === portalType : true,
	);

	// If the currently-selected instance no longer matches the type, reset.
	useEffect(() => {
		if (portalInstanceId && !portals.find((p) => p.id === portalInstanceId)) {
			setPortalInstanceId("");
		}
	}, [portalType, portals, portalInstanceId]);

	// Auto-populate redirect URL from the picked portal instance (one less thing
	// the admin has to remember to type).
	useEffect(() => {
		const pick = portals.find((p) => p.id === portalInstanceId);
		if (pick?.storeUrl && !redirectUrl) {
			setRedirectUrl(pick.storeUrl);
		}
	}, [portalInstanceId, portals, redirectUrl]);

	const onSubmit = async () => {
		if (!email || !redirectUrl) return;
		setSending(true);
		try {
			await partyClient.sendClientInvite({
				partyId,
				email,
				redirectUrl,
				portalInstanceId,
				portalType,
			});
			toast.success(t("connect_client_sent"));
			qc.invalidateQueries({ queryKey: ["client-invites", partyId] });
			setEmail("");
			setRedirectUrl("");
			setPortalInstanceId("");
		} catch (e: any) {
			toast.error(e?.message ?? t("connect_client_failed"));
		} finally {
			setSending(false);
		}
	};

	const statusBadge = (status: string) => {
		switch (status) {
			case "claimed":
				return (
					<Badge variant="default" className="text-[10px]">
						{t("invite_claimed")}
					</Badge>
				);
			case "expired":
				return (
					<Badge variant="secondary" className="text-[10px]">
						{t("invite_expired")}
					</Badge>
				);
			default:
				return (
					<Badge variant="outline" className="text-[10px]">
						{t("invite_pending")}
					</Badge>
				);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{t("connect_client_title")}</DialogTitle>
					<DialogDescription>{t("connect_client_desc")}</DialogDescription>
				</DialogHeader>
				<div className="space-y-3">
					<div>
						<Label>{t("connect_client_email")}</Label>
						<Input
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder="client@example.com"
							type="email"
						/>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<Label>{t("connect_client_portal_type")}</Label>
							<Select value={portalType} onValueChange={setPortalType}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="customer">
										{t("portal_type_customer")}
									</SelectItem>
									<SelectItem value="supplier">
										{t("portal_type_supplier")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label>{t("connect_client_portal_instance")}</Label>
							<Select
								value={portalInstanceId || "__none__"}
								onValueChange={(v) =>
									setPortalInstanceId(v === "__none__" ? "" : v)
								}
							>
								<SelectTrigger>
									<SelectValue
										placeholder={t(
											"connect_client_portal_instance_placeholder",
										)}
									/>
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="__none__">
										{t("connect_client_portal_instance_none")}
									</SelectItem>
									{portals.map((p) => (
										<SelectItem key={p.id} value={p.id}>
											{p.name || p.slug || p.id.slice(0, 8)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<div>
						<Label>{t("connect_client_redirect")}</Label>
						<Input
							value={redirectUrl}
							onChange={(e) => setRedirectUrl(e.target.value)}
							placeholder="https://stockvision.malevich.ai"
						/>
						<p className="text-xs text-muted-foreground mt-1">
							{t("connect_client_redirect_hint")}
						</p>
					</div>
					<Button
						onClick={onSubmit}
						disabled={sending || !email || !redirectUrl}
						className="w-full"
					>
						{sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
						{t("connect_client_send")}
					</Button>
				</div>

				{(invites?.invites?.length ?? 0) > 0 && (
					<div className="border-t pt-3 mt-2">
						<Label className="text-xs text-muted-foreground">
							{t("invite_history")}
						</Label>
						<div className="mt-2 space-y-2">
							{invites!.invites.map((inv: any) => (
								<div
									key={inv.id}
									className="flex items-center justify-between text-xs border rounded px-2 py-1.5"
								>
									<div className="truncate">
										<span className="font-medium">{inv.email}</span>
										<span className="text-muted-foreground ml-2">
											→ {inv.redirectUrl}
										</span>
									</div>
									{statusBadge(inv.status)}
								</div>
							))}
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

/* ── Work Items ── */

export function _PartyWorkItems({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const { data, isLoading } = useQuery({
		queryKey: ["party-work-items", partyId],
		queryFn: async () => {
			const res = await workItemClient.listWorkItems({
				page: { pageSize: 50, pageToken: "" },
				search: "",
				partyId,
			});
			return res.workItems ?? [];
		},
	});

	if (isLoading) {
		return (
			<div className="space-y-2">
				{[1, 2].map((i) => (
					<Skeleton key={i} className="h-12 w-full rounded-md" />
				))}
			</div>
		);
	}

	const items = data ?? [];
	if (items.length === 0) {
		return (
			<p className="text-sm text-muted-foreground py-2">{t("no_work_items")}</p>
		);
	}

	return (
		<div className="space-y-1.5">
			{items.map((wi) => (
				<Link
					key={wi.id}
					to="/work-items/$id"
					params={{ id: wi.id }}
					search={{}}
					className="flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-accent transition-colors"
				>
					<CheckSquare className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
					<div className="flex-1 min-w-0">
						<p className="text-sm font-medium truncate">{wi.title}</p>
						<p className="text-[11px] text-muted-foreground">
							{wi.customer?.partyId === partyId
								? t("wi_role_customer")
								: t("wi_role_assigned")}
						</p>
					</div>
				</Link>
			))}
		</div>
	);
}

/* ── Orders ── */

export function _PartyOrders({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const { data, isLoading } = useQuery({
		queryKey: ["party-orders", partyId],
		queryFn: async () => {
			const res = await orderClient.listOrders({
				page: { pageSize: 50, pageToken: "" },
				search: "",
			});
			return res.orders.filter((o) => o.customerPartyId === partyId);
		},
	});

	if (isLoading) {
		return (
			<div className="space-y-2">
				{[1, 2].map((i) => (
					<Skeleton key={i} className="h-12 w-full rounded-md" />
				))}
			</div>
		);
	}

	const orders = data ?? [];
	if (orders.length === 0) {
		return (
			<p className="text-sm text-muted-foreground py-2">{t("no_orders")}</p>
		);
	}

	return (
		<div className="space-y-1.5">
			{orders.map((o) => (
				<Link
					key={o.id}
					to="/commerce/orders/$id"
					params={{ id: o.id }}
					className="flex items-center gap-3 rounded-md border px-3 py-2 hover:bg-accent transition-colors"
				>
					<ShoppingCart className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
					<div className="flex-1 min-w-0">
						<p className="text-sm font-medium truncate">
							{o.title || t("order_title_fallback", { seqNo: o.seqNo })}
						</p>
					</div>
				</Link>
			))}
		</div>
	);
}

/* ── Custom Fields (Attio-style) ── */

function PartyCustomFields({
	partyId,
	partyKind,
	attrs,
	onSaved,
}: {
	partyId: string;
	partyKind: number;
	attrs: Record<string, unknown>;
	onSaved: () => void;
}) {
	const { t } = useTranslation("parties");
	const [saving, setSaving] = useState(false);
	const kindStr =
		partyKind === 1 ? "person" : partyKind === 2 ? "organization" : "person";

	const { data } = useQuery({
		queryKey: ["party-field-defs"],
		queryFn: () => partyClient.listFieldDefinitions({}),
	});

	const fields: FieldDef[] = (data?.fieldDefinitions ?? [])
		.filter((f: any) => {
			const appliesTo = f.appliesTo ?? [];
			return appliesTo.length === 0 || appliesTo.includes(kindStr);
		})
		.map((f: any) => ({
			id: f.id,
			name: f.name,
			displayName: f.displayName,
			fieldType: f.fieldType,
			options: f.options?.toJson?.() ?? {},
			isRequired: f.isRequired,
		}));

	const handleSave = async (fieldName: string, value: unknown) => {
		setSaving(true);
		try {
			const newAttrs = { ...attrs, [fieldName]: value };
			await partyClient.updateParty({
				id: partyId,
				attrs: Struct.fromJson(newAttrs as any),
			});
			onSaved();
			toast.success(t("toast_saved"));
		} catch (e) {
			console.error("updateParty field failed", e);
			toast.error(t("save_failed"));
		} finally {
			setSaving(false);
		}
	};

	// Merge: defined fields first, then unmatched attrs as plain text
	const definedNames = new Set(fields.map((f) => f.name));
	const extraAttrs = Object.entries(attrs).filter(
		([k]) => !definedNames.has(k),
	);

	if (fields.length === 0 && extraAttrs.length === 0) return null;

	return (
		<Card className="px-4 py-3 gap-1">
			<h3 className="text-sm font-medium mb-1">{t("fields_title")}</h3>
			{fields.map((field) => (
				<PartyFieldRenderer
					key={field.id}
					field={field}
					value={attrs[field.name]}
					onSave={(v) => handleSave(field.name, v)}
					saving={saving}
				/>
			))}
			{extraAttrs.map(([key, val]) => (
				<div key={key} className="flex items-center justify-between py-1.5">
					<span className="text-xs text-muted-foreground w-32 shrink-0">
						{key}
					</span>
					<span className="text-sm truncate">{String(val)}</span>
				</div>
			))}
		</Card>
	);
}

/* ── Timeline + Comments ── */

function PartyTimeline({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const [commentText, setCommentText] = useState("");

	const addCommentMut = useMutation({
		mutationFn: (text: string) =>
			partyClient.addPartyComment({ partyId, text }),
		onSuccess: () => {
			setCommentText("");
			qc.invalidateQueries({ queryKey: ["entity-activity", "party", partyId] });
			toast.success(t("toast_comment_added"));
		},
		onError: () => toast.error(t("save_failed")),
	});

	return (
		<div className="space-y-3">
			<div className="flex gap-2">
				<Textarea
					value={commentText}
					onChange={(e) => setCommentText(e.target.value)}
					placeholder={t("comment_placeholder")}
					className="text-sm min-h-[60px]"
					rows={2}
				/>
			</div>
			{commentText.trim() && (
				<div className="flex justify-end">
					<Button
						size="sm"
						className="h-7 text-xs"
						disabled={addCommentMut.isPending}
						onClick={() => addCommentMut.mutate(commentText)}
					>
						{addCommentMut.isPending && (
							<Loader2 className="h-3 w-3 mr-1 animate-spin" />
						)}
						{t("comment_send")}
					</Button>
				</div>
			)}
			<ActivityFeed entityType="party" entityId={partyId} />
		</div>
	);
}

/* ── Linked Records ── */

function LinkedRecordsCard({
	partyId,
	showOrders,
	showWorkItems,
}: { partyId: string; showOrders: boolean; showWorkItems: boolean }) {
	const { t } = useTranslation("parties");

	const { data: ordersData } = useQuery({
		queryKey: ["party-orders-chips", partyId],
		queryFn: async () => {
			const res = await orderClient.listOrders({
				page: { pageSize: 10 },
				filter: {
					filters: [
						{
							field: "customer_party_id",
							op: "eq",
							value: { kind: { case: "stringValue", value: partyId } },
						},
					],
				},
			});
			return res.orders ?? [];
		},
		enabled: showOrders,
	});

	const { data: workItemsData } = useQuery({
		queryKey: ["party-work-items-chips", partyId],
		queryFn: async () => {
			const res = await workItemClient.listWorkItems({
				page: { pageSize: 10, pageToken: "" },
				search: "",
				partyId,
			});
			return res.workItems ?? [];
		},
		enabled: showWorkItems,
	});

	const orders = ordersData ?? [];
	const workItems = workItemsData ?? [];

	if (orders.length === 0 && workItems.length === 0) return null;

	return (
		<Card className="px-4 py-3 gap-3">
			<h3 className="text-sm font-medium">{t("linked_records")}</h3>
			<div className="space-y-2">
				{orders.length > 0 && (
					<div>
						<span className="text-xs text-muted-foreground">{t("orders")}</span>
						<div className="flex flex-wrap gap-1.5 mt-1">
							{orders.map((o: any) => (
								<EntityChip
									key={o.id}
									entityType="order"
									entityId={o.id}
									displayText={
										o.sequenceNumber
											? `#${o.sequenceNumber}`
											: o.title || o.id.slice(0, 8)
									}
								/>
							))}
						</div>
					</div>
				)}
				{workItems.length > 0 && (
					<div>
						<span className="text-xs text-muted-foreground">
							{t("work_items")}
						</span>
						<div className="flex flex-wrap gap-1.5 mt-1">
							{workItems.map((wi: any) => (
								<EntityChip
									key={wi.id}
									entityType="work_item"
									entityId={wi.id}
									displayText={wi.title || wi.id.slice(0, 8)}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</Card>
	);
}

/* ── Editable Attributes Card (legacy fallback) ── */

export function _EditableAttributesCard({
	partyId,
	attrs,
	onSaved,
}: { partyId: string; attrs: Record<string, unknown>; onSaved: () => void }) {
	const { t } = useTranslation("parties");
	const [editing, setEditing] = useState(false);
	const [entries, setEntries] = useState<[string, string][]>([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setEntries(Object.entries(attrs).map(([k, v]) => [k, String(v)]));
	}, [attrs]);

	const handleSave = async () => {
		setSaving(true);
		try {
			const newAttrs: Record<string, string> = {};
			for (const [k, v] of entries) {
				if (k.trim()) newAttrs[k.trim()] = v;
			}
			await partyClient.updateParty({
				id: partyId,
				attrs: Struct.fromJson(newAttrs),
			});
			setEditing(false);
			onSaved();
			toast.success(t("toast_saved"));
		} catch (e) {
			console.error("updateParty attrs failed", e);
			toast.error(t("save_failed"));
		} finally {
			setSaving(false);
		}
	};

	const addRow = () => setEntries((prev) => [...prev, ["", ""]]);

	const updateEntry = (idx: number, col: 0 | 1, val: string) => {
		setEntries((prev) => {
			const next = [...prev];
			next[idx] = [...next[idx]] as [string, string];
			next[idx][col] = val;
			return next;
		});
	};

	const removeEntry = (idx: number) => {
		setEntries((prev) => prev.filter((_, i) => i !== idx));
	};

	if (!editing && Object.keys(attrs).length === 0) {
		return (
			<Card className="px-4 py-3 gap-3">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium">{t("attributes")}</h3>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 text-xs"
						onClick={() => {
							setEntries([["", ""]]);
							setEditing(true);
						}}
					>
						<Plus className="h-3 w-3 mr-1" />
						{t("common:add")}
					</Button>
				</div>
			</Card>
		);
	}

	if (!editing) {
		return (
			<Card className="px-4 py-3 gap-3">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium">{t("attributes")}</h3>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 text-xs"
						onClick={() => setEditing(true)}
					>
						<Edit2 className="h-3 w-3 mr-1" />
						{t("common:edit")}
					</Button>
				</div>
				<dl className="grid grid-cols-2 gap-x-6 gap-y-2">
					{Object.entries(attrs).map(([key, val]) => (
						<div key={key}>
							<dt className="text-xs text-muted-foreground">{key}</dt>
							<dd className="text-sm">{String(val)}</dd>
						</div>
					))}
				</dl>
			</Card>
		);
	}

	return (
		<Card className="px-4 py-3 gap-3">
			<h3 className="text-sm font-medium">{t("attributes")}</h3>
			<div className="space-y-2">
				{entries.map(([key, val], idx) => (
					<div key={idx} className="flex items-center gap-2">
						<Input
							className="h-7 text-xs flex-1"
							placeholder={t("attr_key")}
							value={key}
							onChange={(e) => updateEntry(idx, 0, e.target.value)}
						/>
						<Input
							className="h-7 text-xs flex-1"
							placeholder={t("attr_value")}
							value={val}
							onChange={(e) => updateEntry(idx, 1, e.target.value)}
						/>
						<Button
							variant="ghost"
							size="sm"
							className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
							onClick={() => removeEntry(idx)}
						>
							<X className="h-3.5 w-3.5" />
						</Button>
					</div>
				))}
			</div>
			<div className="flex items-center gap-2">
				<Button
					variant="ghost"
					size="sm"
					className="h-7 text-xs"
					onClick={addRow}
				>
					<Plus className="h-3 w-3 mr-1" />
					{t("attr_add_row")}
				</Button>
				<div className="flex-1" />
				<Button
					size="sm"
					className="h-7 text-xs"
					onClick={handleSave}
					disabled={saving}
				>
					{saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
					{t("common:save")}
				</Button>
				<Button
					variant="ghost"
					size="sm"
					className="h-7 text-xs"
					onClick={() => setEditing(false)}
				>
					{t("common:cancel")}
				</Button>
			</div>
		</Card>
	);
}

/* ── Commercial Info Card ── */

const TIER_OPTIONS = ["strategic", "key", "standard", "transactional"] as const;

function CommercialInfoCard({
	party,
	onSaved,
	mode,
}: {
	party: any;
	onSaved: () => void;
	mode: "sales" | "procurement";
}) {
	const { t } = useTranslation("parties");
	const [editing, setEditing] = useState(false);
	const [tier, setTier] = useState(party.pricingTier || "");
	const [terms, setTerms] = useState(String(party.paymentTermsDays || ""));
	const [saving, setSaving] = useState(false);
	const showTier = mode === "sales";
	const titleKey = mode === "sales" ? "sales_terms" : "procurement_terms";

	useEffect(() => {
		setTier(party.pricingTier || "");
		setTerms(String(party.paymentTermsDays || ""));
	}, [party.pricingTier, party.paymentTermsDays]);

	const handleSave = async () => {
		setSaving(true);
		try {
			await partyClient.updateParty({
				id: party.id,
				pricingTier: showTier ? tier || undefined : undefined,
				paymentTermsDays: Number.parseInt(terms) || 0,
			});
			setEditing(false);
			onSaved();
			toast.success(t("toast_saved"));
		} catch (e) {
			console.error("updateParty commercial failed", e);
			toast.error(t("save_failed"));
		} finally {
			setSaving(false);
		}
	};

	if (!editing) {
		return (
			<Card className="px-4 py-3 gap-3">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium flex items-center gap-1.5">
						<CreditCard className="h-4 w-4 text-muted-foreground" />
						{t(titleKey)}
					</h3>
					<Button
						variant="ghost"
						size="sm"
						className="h-7 text-xs"
						onClick={() => setEditing(true)}
					>
						<Edit2 className="h-3 w-3 mr-1" />
						{t("common:edit")}
					</Button>
				</div>
				<div className="grid grid-cols-2 gap-x-6 gap-y-2">
					{showTier && (
						<div>
							<dt className="text-xs text-muted-foreground">
								{t("pricing_tier")}
							</dt>
							<dd className="text-sm font-medium">
								{party.pricingTier
									? String(t(`tier_${party.pricingTier}`, party.pricingTier))
									: "—"}
							</dd>
						</div>
					)}
					<div>
						<dt className="text-xs text-muted-foreground">
							{t("payment_terms")}
						</dt>
						<dd className="text-sm font-medium">
							{party.paymentTermsDays > 0
								? t("payment_terms_days", { days: party.paymentTermsDays })
								: "—"}
						</dd>
					</div>
				</div>
			</Card>
		);
	}

	return (
		<Card className="px-4 py-3 gap-3">
			<h3 className="text-sm font-medium flex items-center gap-1.5">
				<CreditCard className="h-4 w-4 text-muted-foreground" />
				{t(titleKey)}
			</h3>
			<div className="grid grid-cols-2 gap-3">
				{showTier && (
					<div className="space-y-1">
						<Label className="text-xs text-muted-foreground">
							{t("pricing_tier")}
						</Label>
						<Select
							value={tier || "_none"}
							onValueChange={(v) => setTier(v === "_none" ? "" : v)}
						>
							<SelectTrigger className="h-8 text-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="_none">—</SelectItem>
								{TIER_OPTIONS.map((o) => (
									<SelectItem key={o} value={o}>
										{t(`tier_${o}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				)}
				<div className="space-y-1">
					<Label className="text-xs text-muted-foreground">
						{t("payment_terms")}
					</Label>
					<Input
						type="number"
						className="h-8 text-sm"
						value={terms}
						onChange={(e) => setTerms(e.target.value)}
						placeholder="0"
					/>
				</div>
			</div>
			<div className="flex gap-2">
				<Button
					size="sm"
					className="h-7 text-xs"
					onClick={handleSave}
					disabled={saving}
				>
					{saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
					{t("common:save")}
				</Button>
				<Button
					size="sm"
					variant="ghost"
					className="h-7 text-xs"
					onClick={() => setEditing(false)}
				>
					{t("common:cancel")}
				</Button>
			</div>
		</Card>
	);
}

/* ── Inline Channel Add ── */

function InlineChannelAdd({
	partyId,
	onAdded,
}: { partyId: string; onAdded: () => void }) {
	const { t } = useTranslation("parties");
	const [value, setValue] = useState("");
	const [saving, setSaving] = useState(false);

	const handleAdd = async () => {
		const trimmed = value.trim();
		if (!trimmed || saving) return;
		// Auto-detect: @ → email (3), +/digits → phone (5)
		const kind = trimmed.includes("@") ? 3 : 5;
		setSaving(true);
		try {
			await partyClient.createChannel({
				partyId,
				kind: kind as any,
				identifier: trimmed,
			});
			setValue("");
			onAdded();
			toast.success(t("toast_channel_added"));
		} catch (e) {
			console.error("createChannel failed", e);
			toast.error(t("add_channel_failed"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="relative">
			<Input
				placeholder={t("channel_quick_placeholder")}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") handleAdd();
				}}
				className="h-7 text-xs pr-8"
				disabled={saving}
			/>
			{saving && (
				<Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
			)}
		</div>
	);
}

/* ── Add Channel Dialog ── */

function AddChannelDialog({
	open,
	onOpenChange,
	partyId,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	partyId: string;
	onCreated: () => void;
}) {
	const { t } = useTranslation("parties");
	const [kind, setKind] = useState("3");
	const [scope, setScope] = useState("1");
	const [identifier, setIdentifier] = useState("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async () => {
		if (!identifier.trim()) return;
		setSaving(true);
		setError(null);
		try {
			await partyClient.createChannel({
				partyId,
				kind: Number.parseInt(kind) as ChannelKind,
				scope: Number.parseInt(scope) as ChannelScope,
				identifier: identifier.trim(),
			});
			setIdentifier("");
			onOpenChange(false);
			onCreated();
			toast.success(t("toast_channel_added"));
		} catch (e) {
			setError(e instanceof Error ? e.message : t("add_channel_failed"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("add_channel_title")}</DialogTitle>
					<DialogDescription>{t("add_channel_description")}</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-1.5">
						<Label>{t("label_type")}</Label>
						<Select value={kind} onValueChange={setKind}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="3">Email</SelectItem>
								<SelectItem value="5">{t("channel_phone")}</SelectItem>
								<SelectItem value="1">WhatsApp</SelectItem>
								<SelectItem value="2">Telegram</SelectItem>
								<SelectItem value="4">SMS</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label>{t("channel_scope_label")}</Label>
						<Select value={scope} onValueChange={setScope}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="1">{t("channel_scope_work")}</SelectItem>
								<SelectItem value="2">{t("channel_scope_personal")}</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label>{t("channel_identifier_label")}</Label>
						<Input
							placeholder={kind === "3" ? "email@example.com" : "+79001234567"}
							value={identifier}
							onChange={(e) => setIdentifier(e.target.value)}
						/>
					</div>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<DialogFooter>
					<Button
						onClick={handleSubmit}
						disabled={!identifier.trim() || saving}
					>
						{saving ? t("adding") : t("common:add")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/* ── Role Badges ── */

const ALL_ROLES = [
	"customer",
	"supplier",
	"competitor",
	"partner",
	"lead",
] as const;

function RoleBadges({
	partyId,
	roles,
	onChanged,
}: { partyId: string; roles: string[]; onChanged: () => void }) {
	const { t } = useTranslation("parties");
	const [busy, setBusy] = useState(false);

	const removeRole = async (role: string) => {
		setBusy(true);
		try {
			await partyClient.removePartyRole({ partyId, role });
			onChanged();
			toast.success(t("toast_role_removed"));
		} catch (e) {
			console.error("removePartyRole failed", e);
			toast.error(t("role_change_failed"));
		} finally {
			setBusy(false);
		}
	};

	const addRole = async (role: string) => {
		setBusy(true);
		try {
			await partyClient.addPartyRole({ partyId, role });
			onChanged();
			toast.success(t("toast_role_added"));
		} catch (e) {
			console.error("addPartyRole failed", e);
			toast.error(t("role_change_failed"));
		} finally {
			setBusy(false);
		}
	};

	const available = ALL_ROLES.filter((r) => !roles.includes(r));

	return (
		<>
			{roles.map((r) => (
				<Badge
					key={r}
					variant="default"
					className="text-[10px] gap-0.5 pr-1 cursor-pointer"
					onClick={() => !busy && removeRole(r)}
				>
					{t(`role_${r}`, r)}
					<X className="h-2.5 w-2.5 ml-0.5" />
				</Badge>
			))}
			{available.length > 0 && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							type="button"
							variant="outline"
							disabled={busy}
							className="h-5 rounded px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
						>
							<Plus className="h-2.5 w-2.5 inline mr-0.5" />
							{t("add_role")}
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						{available.map((r) => (
							<DropdownMenuItem key={r} onClick={() => addRole(r)}>
								{t(`role_${r}`, r)}
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</>
	);
}

/* ── Link Party Dialog ── */

function LinkPartyDialog({
	open,
	onOpenChange,
	partyId,
	partyKind,
	linkKind,
	onLinked,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	partyId: string;
	partyKind: number;
	linkKind: "contact" | "owner";
	onLinked: () => void;
}) {
	const { t } = useTranslation("parties");
	const isOrg = partyKind === 2;
	const isContactLink = linkKind === "contact";
	const [search, setSearch] = useState("");
	const [results, setResults] = useState<any[]>([]);
	const [searching, setSearching] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [nextToken, setNextToken] = useState("");
	const [totalCount, setTotalCount] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [role, setRole] = useState("");
	const [saving, setSaving] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const scrollRef = useRef<HTMLDivElement>(null);

	const searchKind = isContactLink
		? isOrg
			? PartyKind.PERSON
			: PartyKind.ORGANIZATION
		: PartyKind.PERSON;

	const doSearch = useCallback(
		async (query: string, pageToken = "", append = false) => {
			if (append) {
				setLoadingMore(true);
			} else {
				setSearching(true);
			}
			try {
				if (!isContactLink) {
					// Owner/responsible: show workspace members directly
					// member.userId IS their party ID (from auth system)
					const memberRes = await memberClient.listMembers({
						search: query.trim(),
					});
					const memberResults = (memberRes.members ?? [])
						.filter((m: any) => m.userId !== partyId)
						.map((m: any) => ({
							id: m.userId, // this IS the party ID
							name: m.name,
							displayName: m.name,
							email: m.email,
							avatarUrl: m.avatarUrl,
							kind: 1,
							role: m.role, // workspace role (admin, member, etc.)
						}));
					setResults(memberResults);
					setHasMore(false);
					setNextToken("");
					if (!append) setTotalCount(memberResults.length);
				} else {
					// Contact link: search parties by kind
					const res = await partyClient.listParties({
						search: query.trim(),
						kind: searchKind,
						page: { pageSize: 30, pageToken },
					});
					const filtered = res.parties.filter((p: any) => p.id !== partyId);
					setResults((prev) => (append ? [...prev, ...filtered] : filtered));
					setNextToken(res.page?.nextPageToken ?? "");
					setHasMore(!!res.page?.nextPageToken);
					if (!append) setTotalCount(res.page?.totalCount ?? 0);
				}
			} catch {
				if (!append) setResults([]);
			} finally {
				setSearching(false);
				setLoadingMore(false);
			}
		},
		[searchKind, isContactLink, partyId],
	);

	// Load initial results on open, debounce search changes
	useEffect(() => {
		if (!open) {
			setSearch("");
			setResults([]);
			setNextToken("");
			setHasMore(false);
			setRole("");
			if (debounceRef.current) clearTimeout(debounceRef.current);
			return;
		}
		// Initial load
		doSearch("");
	}, [open, doSearch]);

	// Debounced search on input change
	useEffect(() => {
		if (!open) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			doSearch(search);
		}, 300);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [search, open, doSearch]);

	const handleScroll = useCallback(() => {
		const el = scrollRef.current;
		if (!el || !hasMore || loadingMore) return;
		if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
			doSearch(search, nextToken, true);
		}
	}, [hasMore, loadingMore, search, nextToken, doSearch]);

	const handleLink = async (targetId: string) => {
		setSaving(true);
		try {
			if (isContactLink) {
				await partyClient.linkParties({
					fromPartyId: isOrg ? partyId : targetId,
					toPartyId: isOrg ? targetId : partyId,
					kind: "contacts_for",
					role: role || "",
				});
			} else {
				await partyClient.linkParties({
					fromPartyId: targetId,
					toPartyId: partyId,
					kind: "manages",
					role: role || "", // empty = backend sets locale-aware default
				});
			}
			onOpenChange(false);
			onLinked();
			toast.success(
				isContactLink
					? t("toast_contact_linked")
					: t("toast_employee_assigned"),
			);
		} catch {
			toast.error(t("toast_link_failed"));
		} finally {
			setSaving(false);
		}
	};

	const title = isContactLink
		? isOrg
			? t("link_contact_org_title")
			: t("link_person_org_title")
		: t("link_owner_title");
	const description = isContactLink
		? isOrg
			? t("link_contact_org_desc")
			: t("link_person_org_desc")
		: t("link_owner_desc");
	const searchPlaceholder = isContactLink
		? isOrg
			? t("search_contact_name")
			: t("search_org_name")
		: t("search_employee_name");

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>
				<div className="space-y-3 py-2">
					<div className="relative">
						<Input
							placeholder={searchPlaceholder}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							autoFocus
						/>
						{searching && (
							<div className="absolute right-3 top-1/2 -translate-y-1/2">
								<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
							</div>
						)}
					</div>
					{!searching && results.length > 0 && totalCount > 0 && (
						<div className="text-[11px] text-muted-foreground mb-1">
							{t("found_count", { count: totalCount })}
						</div>
					)}
					<div
						ref={scrollRef}
						onScroll={handleScroll}
						className="max-h-60 overflow-y-auto rounded-md border"
					>
						{searching && results.length === 0 ? (
							<div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
								<Loader2 className="h-4 w-4 animate-spin" />
								{t("common:loading")}
							</div>
						) : !searching && results.length === 0 ? (
							<div className="py-6 text-center text-sm text-muted-foreground">
								{t("nothing_found")}
							</div>
						) : (
							<>
								{results.map((p: any) => (
									<Button
										key={p.id}
										variant="ghost"
										disabled={saving}
										className="flex w-full items-center gap-2 px-3 py-2 h-auto text-left justify-start rounded-none border-b last:border-b-0"
										onClick={() => handleLink(p.id)}
									>
										{p.kind === 2 ? (
											<Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
										) : (
											<User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
										)}
										<div className="flex flex-col min-w-0 flex-1">
											<div className="flex items-center gap-1.5">
												<span className="truncate">
													{p.displayName || p.name}
												</span>
												{p.role && (
													<Badge
														variant="outline"
														className="text-[10px] shrink-0"
													>
														{p.role}
													</Badge>
												)}
											</div>
											{p.email && (
												<span className="text-xs text-muted-foreground truncate">
													{p.email}
												</span>
											)}
										</div>
										<Link2 className="h-3 w-3 ml-auto text-muted-foreground shrink-0" />
									</Button>
								))}
								{loadingMore && (
									<div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
										{t("loading_more")}
									</div>
								)}
							</>
						)}
					</div>
					<div className="space-y-1.5">
						<Label className="text-xs text-muted-foreground">
							{t("role_label")}
						</Label>
						<Input
							placeholder={
								isContactLink
									? t("role_placeholder_contact")
									: t("role_placeholder_owner")
							}
							value={role}
							onChange={(e) => setRole(e.target.value)}
							className="h-8 text-sm"
						/>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

/* ── Export Templates ── */

export function _PartyExportTemplates({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const navigate = useNavigate();

	const { data, isLoading } = useQuery({
		queryKey: ["party-export-templates", partyId],
		queryFn: () =>
			exportClient.listExportTemplates({
				partyId,
				page: { pageSize: 50, pageToken: "" },
			}),
	});

	const templates = data?.templates ?? [];

	const createMut = useMutation({
		mutationFn: async () => {
			// Fetch locale-aware default columns from backend
			const { columns: defaultCols } =
				await exportClient.getDefaultExportColumns({});
			const colsJson = defaultCols.map((c) => ({
				field: c.field,
				header: c.header,
				width: c.width,
				...(c.numberFormat ? { number_format: c.numberFormat } : {}),
			}));
			return exportClient.createExportTemplate({
				name: t("export_for_client", { num: templates.length + 1 }),
				scope: "party",
				partyId,
				format: "xlsx",
				// `client_catalog` makes this template usable
				// by the "Скачать прайс" / ExportClientCatalog
				// button. `order` would shape it for an order
				// export and the catalog flow would skip it.
				entityType: "client_catalog",
				isDefault: templates.length === 0,
				columns: Struct.fromJson({
					value: colsJson,
				} as unknown as Parameters<typeof Struct.fromJson>[0]),
				headerConfig: Struct.fromJson({
					show_date: true,
					show_customer: true,
				} as unknown as Parameters<typeof Struct.fromJson>[0]),
				footerConfig: Struct.fromJson({
					show_totals: true,
				} as unknown as Parameters<typeof Struct.fromJson>[0]),
				styling: Struct.fromJson({
					alternate_rows: true,
				} as unknown as Parameters<typeof Struct.fromJson>[0]),
			});
		},
		onSuccess: (res) => {
			qc.invalidateQueries({
				queryKey: ["party-export-templates", partyId],
			});
			// Navigate to the full editor
			navigate({
				to: `/settings/export-templates/${res.id}?partyId=${partyId}`,
			});
		},
	});

	const deleteMut = useMutation({
		mutationFn: (templateId: string) =>
			exportClient.deleteExportTemplate({ id: templateId }),
		onSuccess: () =>
			qc.invalidateQueries({
				queryKey: ["party-export-templates", partyId],
			}),
	});

	if (isLoading) {
		return <Skeleton className="h-10 w-full rounded-md" />;
	}

	if (templates.length === 0) {
		return (
			<div className="flex items-center justify-between py-1">
				<p className="text-sm text-muted-foreground">{t("no_templates")}</p>
				<Button
					size="sm"
					variant="ghost"
					className="h-7 text-xs"
					onClick={() => createMut.mutate()}
					disabled={createMut.isPending}
				>
					<Plus className="h-3 w-3 mr-1" />
					{t("common:create")}
				</Button>
			</div>
		);
	}

	return (
		<div className="space-y-1.5">
			<div className="flex justify-end">
				<Button
					size="sm"
					variant="ghost"
					className="h-7 text-xs"
					onClick={() => createMut.mutate()}
					disabled={createMut.isPending}
				>
					<Plus className="h-3 w-3 mr-1" />
					{t("common:add")}
				</Button>
			</div>
			{templates.map((tmpl) => (
				<div
					key={tmpl.id}
					className="flex items-center justify-between rounded-md border px-3 py-2"
				>
					<div className="flex items-center gap-2 min-w-0">
						<FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
						<span className="text-sm font-medium truncate">{tmpl.name}</span>
						{tmpl.isDefault && (
							<Badge variant="secondary" className="text-[10px] shrink-0">
								<Star className="h-2.5 w-2.5 mr-0.5" />
								{t("default_badge")}
							</Badge>
						)}
					</div>
					<div className="flex items-center gap-1 shrink-0">
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6 text-muted-foreground hover:text-foreground"
							onClick={() =>
								navigate({
									to: `/settings/export-templates/${tmpl.id}?partyId=${partyId}`,
								})
							}
						>
							<Edit2 className="h-3 w-3" />
						</Button>
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6 text-muted-foreground hover:text-destructive"
							onClick={() => deleteMut.mutate(tmpl.id)}
						>
							<Trash2 className="h-3 w-3" />
						</Button>
					</div>
				</div>
			))}
		</div>
	);
}

/* ── Party Preferences Panel ── */

interface PrefState {
	defaultMarkup: number;
	categoryRules: Array<{
		categoryId: string;
		categoryName: string;
		markup: number;
		included: boolean;
	}>;
	brandInclude: string[];
	brandExclude: string[];
	priceMin: number;
	priceMax: number;
	logisticsNotes: string;
	freeTextRules: string;
	currency: string;
	defaultExportTemplateId: string;
	seasonalRules: Array<{
		id: string;
		name: string;
		startDate: string;
		endDate: string;
		categoryIds: string[];
		brandIds: string[];
		markup: number;
	}>;
}

function BrandAutocomplete({
	value,
	onChange,
	placeholder,
	badgeVariant = "secondary",
	badgeClassName = "",
}: {
	value: string[];
	onChange: (brands: string[]) => void;
	placeholder?: string;
	badgeVariant?: "secondary" | "outline";
	badgeClassName?: string;
}) {
	const [query, setQuery] = useState("");
	const [results, setResults] = useState<string[]>([]);
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!query.trim()) {
			setResults([]);
			return;
		}
		const timer = setTimeout(async () => {
			try {
				const res = await catalogClient.searchBrands({
					query: query.trim(),
					limit: 10,
				});
				setResults(res.brands.filter((b) => !value.includes(b)));
				setOpen(true);
			} catch {
				setResults([]);
			}
		}, 200);
		return () => clearTimeout(timer);
	}, [query, value]);

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	function addBrand(brand: string) {
		if (!value.includes(brand)) {
			onChange([...value, brand]);
		}
		setQuery("");
		setOpen(false);
	}

	function removeBrand(brand: string) {
		onChange(value.filter((b) => b !== brand));
	}

	return (
		<div className="space-y-1.5">
			<div className="flex flex-wrap gap-1">
				{value.map((b) => (
					<Badge
						key={b}
						variant={badgeVariant}
						className={`text-[11px] gap-1 cursor-pointer hover:bg-destructive/10 ${badgeClassName}`}
						onClick={() => removeBrand(b)}
					>
						{b}
						<X className="h-2.5 w-2.5" />
					</Badge>
				))}
			</div>
			<div className="relative" ref={ref}>
				<Input
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onFocus={() => results.length > 0 && setOpen(true)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							const v = query.trim();
							if (v && !value.includes(v)) {
								addBrand(v);
							}
						}
					}}
					placeholder={placeholder}
					className="h-7 text-xs"
				/>
				{open && results.length > 0 && (
					<div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-40 overflow-y-auto">
						{results.map((b) => (
							<Button
								key={b}
								variant="ghost"
								className="w-full justify-start px-3 py-1.5 h-auto rounded-none"
								onClick={() => addBrand(b)}
							>
								{b}
							</Button>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

const CURRENCY_CODES = ["RUB", "USD", "EUR", "CNY", "KZT"];

function prefsToPrefState(p: PartyPreferences | undefined | null): PrefState {
	return {
		defaultMarkup: p?.defaultMarkup || 1.0,
		categoryRules: (p?.categoryRules ?? []).map((r) => ({
			categoryId: r.categoryId ?? "",
			categoryName: r.categoryName ?? "",
			markup: r.markup ?? 1.0,
			included: r.included ?? true,
		})),
		brandInclude: [...(p?.brandInclude ?? [])],
		brandExclude: [...(p?.brandExclude ?? [])],
		priceMin: p?.priceMin ?? 0,
		priceMax: p?.priceMax ?? 0,
		logisticsNotes: p?.logisticsNotes ?? "",
		freeTextRules: p?.freeTextRules ?? "",
		currency: p?.currency ?? "",
		defaultExportTemplateId: p?.defaultExportTemplateId ?? "",
		seasonalRules: (p?.seasonalRules ?? []).map((r) => ({
			id: r.id ?? "",
			name: r.name ?? "",
			startDate: r.startDate ?? "",
			endDate: r.endDate ?? "",
			categoryIds: [...(r.categoryIds ?? [])],
			brandIds: [...(r.brandIds ?? [])],
			markup: r.markup ?? 1.0,
		})),
	};
}

function PartyPreferencesPanel({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const [expanded, setExpanded] = useState(false);
	const [prefs, setPrefs] = useState<PrefState>({
		defaultMarkup: 1.0,
		categoryRules: [],
		brandInclude: [],
		brandExclude: [],
		priceMin: 0,
		priceMax: 0,
		logisticsNotes: "",
		freeTextRules: "",
		currency: "",
		defaultExportTemplateId: "",
		seasonalRules: [],
	});
	const [dirty, setDirty] = useState(false);

	const { data: prefsData, isLoading } = useQuery({
		queryKey: ["party-preferences", partyId],
		queryFn: () => partyClient.getPartyPreferences({ partyId }),
	});

	const { data: templatesData } = useQuery({
		queryKey: ["party-export-templates-list", partyId],
		queryFn: () =>
			exportClient.listExportTemplates({
				partyId,
				page: { pageSize: 100, pageToken: "" },
			}),
		enabled: expanded,
	});

	useEffect(() => {
		if (prefsData) {
			setPrefs(prefsToPrefState(prefsData));
			setDirty(false);
		}
	}, [prefsData]);

	const saveMut = useMutation({
		mutationFn: () =>
			partyClient.updatePartyPreferences({
				partyId,
				preferences: {
					defaultMarkup: prefs.defaultMarkup,
					categoryRules: prefs.categoryRules,
					brandInclude: prefs.brandInclude,
					brandExclude: prefs.brandExclude,
					priceMin: prefs.priceMin,
					priceMax: prefs.priceMax,
					logisticsNotes: prefs.logisticsNotes,
					freeTextRules: prefs.freeTextRules,
					currency: prefs.currency,
					defaultExportTemplateId: prefs.defaultExportTemplateId,
					seasonalRules: prefs.seasonalRules.map((r) => ({
						id: r.id,
						name: r.name,
						startDate: r.startDate,
						endDate: r.endDate,
						categoryIds: r.categoryIds,
						brandIds: r.brandIds,
						markup: r.markup,
					})),
				},
			}),
		onSuccess: () => {
			setDirty(false);
			qc.invalidateQueries({ queryKey: ["party-preferences", partyId] });
			toast.success(t("toast_saved"));
		},
		onError: () => toast.error(t("save_failed")),
	});

	function update<K extends keyof PrefState>(key: K, value: PrefState[K]) {
		setPrefs((prev) => ({ ...prev, [key]: value }));
		setDirty(true);
	}

	function addCategoryRule() {
		update("categoryRules", [
			...prefs.categoryRules,
			{ categoryId: "", categoryName: "", markup: 1.0, included: true },
		]);
	}

	function updateCategoryRule(
		idx: number,
		patch: Partial<PrefState["categoryRules"][number]>,
	) {
		update(
			"categoryRules",
			prefs.categoryRules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
		);
	}

	function removeCategoryRule(idx: number) {
		update(
			"categoryRules",
			prefs.categoryRules.filter((_, i) => i !== idx),
		);
	}

	if (isLoading) {
		return <Skeleton className="h-10 w-full rounded-md" />;
	}

	if (!expanded) {
		const hasPrefs =
			prefs.defaultMarkup !== 1.0 ||
			prefs.categoryRules.length > 0 ||
			prefs.brandInclude.length > 0 ||
			prefs.brandExclude.length > 0 ||
			prefs.freeTextRules ||
			prefs.currency;

		return (
			<div className="flex items-center justify-between py-1">
				<p className="text-sm text-muted-foreground">
					{hasPrefs ? t("prefs_set") : t("prefs_not_set")}
				</p>
				<Button
					size="sm"
					variant="ghost"
					className="h-7 text-xs"
					onClick={() => setExpanded(true)}
				>
					<Settings className="h-3 w-3 mr-1" />
					{hasPrefs ? t("prefs_edit") : t("prefs_setup")}
				</Button>
			</div>
		);
	}

	const templates = templatesData?.templates ?? [];

	return (
		<div className="space-y-4">
			{/* Markup */}
			<div className="space-y-1.5">
				<Label className="text-xs">{t("default_markup")}</Label>
				<div className="flex items-center gap-2">
					<Input
						type="number"
						step="0.01"
						min="0.5"
						max="5.0"
						value={prefs.defaultMarkup}
						onChange={(e) =>
							update("defaultMarkup", Number.parseFloat(e.target.value) || 1.0)
						}
						className="h-8 text-sm w-24"
					/>
					<span className="text-xs text-muted-foreground">
						{prefs.defaultMarkup === 1.0
							? t("no_markup")
							: prefs.defaultMarkup > 1.0
								? `+${((prefs.defaultMarkup - 1) * 100).toFixed(0)}%`
								: `-${((1 - prefs.defaultMarkup) * 100).toFixed(0)}%`}
					</span>
				</div>
			</div>

			{/* Category rules */}
			<div className="space-y-1.5">
				<div className="flex items-center justify-between">
					<Label className="text-xs">{t("category_rules")}</Label>
					<Button
						size="sm"
						variant="ghost"
						className="h-6 text-[11px]"
						onClick={addCategoryRule}
					>
						<Plus className="h-3 w-3 mr-0.5" />
						{t("common:add")}
					</Button>
				</div>
				{prefs.categoryRules.length === 0 ? (
					<p className="text-xs text-muted-foreground py-1">
						{t("no_category_rules")}
					</p>
				) : (
					<div className="space-y-1.5">
						{prefs.categoryRules.map((rule, idx) => (
							<div key={idx} className="flex items-center gap-1.5">
								<Input
									value={rule.categoryName}
									onChange={(e) =>
										updateCategoryRule(idx, { categoryName: e.target.value })
									}
									placeholder={t("category_placeholder")}
									className="h-7 text-xs flex-1"
								/>
								<Input
									type="number"
									step="0.01"
									value={rule.markup}
									onChange={(e) =>
										updateCategoryRule(idx, {
											markup: Number.parseFloat(e.target.value) || 1.0,
										})
									}
									className="h-7 text-xs w-20"
								/>
								<Checkbox
									checked={rule.included}
									onCheckedChange={(v) =>
										updateCategoryRule(idx, { included: !!v })
									}
								/>
								<span className="text-[10px] text-muted-foreground whitespace-nowrap">
									{rule.included ? t("included") : t("excluded")}
								</span>
								<Button
									variant="ghost"
									size="icon"
									className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
									onClick={() => removeCategoryRule(idx)}
								>
									<X className="h-3 w-3" />
								</Button>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Brand include */}
			<div className="space-y-1.5">
				<Label className="text-xs">{t("brands_include")}</Label>
				<BrandAutocomplete
					value={prefs.brandInclude}
					onChange={(v) => update("brandInclude", v)}
					placeholder={t("brands_include_placeholder")}
					badgeVariant="secondary"
				/>
			</div>

			{/* Brand exclude */}
			<div className="space-y-1.5">
				<Label className="text-xs">{t("brands_exclude")}</Label>
				<BrandAutocomplete
					value={prefs.brandExclude}
					onChange={(v) => update("brandExclude", v)}
					placeholder={t("brands_exclude_placeholder")}
					badgeVariant="outline"
					badgeClassName="text-destructive/70"
				/>
			</div>

			{/* Price range */}
			<div className="grid grid-cols-2 gap-3">
				<div className="space-y-1">
					<Label className="text-xs">{t("min_price")}</Label>
					<Input
						type="number"
						step="0.01"
						min="0"
						value={prefs.priceMin || ""}
						onChange={(e) =>
							update("priceMin", Number.parseFloat(e.target.value) || 0)
						}
						placeholder="0"
						className="h-8 text-sm"
					/>
				</div>
				<div className="space-y-1">
					<Label className="text-xs">{t("max_price")}</Label>
					<Input
						type="number"
						step="0.01"
						min="0"
						value={prefs.priceMax || ""}
						onChange={(e) =>
							update("priceMax", Number.parseFloat(e.target.value) || 0)
						}
						placeholder={t("no_limit_placeholder")}
						className="h-8 text-sm"
					/>
				</div>
			</div>

			{/* Currency */}
			<div className="space-y-1.5">
				<Label className="text-xs">{t("currency_label")}</Label>
				<Select
					value={prefs.currency || "__none__"}
					onValueChange={(v) => update("currency", v === "__none__" ? "" : v)}
				>
					<SelectTrigger className="h-8 text-sm">
						<SelectValue placeholder={t("currency_default")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="__none__">{t("currency_default")}</SelectItem>
						{CURRENCY_CODES.map((c) => (
							<SelectItem key={c} value={c}>
								{c}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* Default export template */}
			{templates.length > 0 && (
				<div className="space-y-1.5">
					<Label className="text-xs">{t("default_export_template")}</Label>
					<Select
						value={prefs.defaultExportTemplateId || "__none__"}
						onValueChange={(v) =>
							update("defaultExportTemplateId", v === "__none__" ? "" : v)
						}
					>
						<SelectTrigger className="h-8 text-sm">
							<SelectValue placeholder={t("not_selected")} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="__none__">{t("not_selected")}</SelectItem>
							{templates.map((tmpl) => (
								<SelectItem key={tmpl.id} value={tmpl.id}>
									{tmpl.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}

			{/* Seasonal rules */}
			<div className="space-y-1.5">
				<div className="flex items-center justify-between">
					<Label className="text-xs">
						{t("seasonal_rules", { defaultValue: "Сезонные правила" })}
					</Label>
					<Button
						size="sm"
						variant="ghost"
						className="h-6 text-[11px]"
						onClick={() =>
							update("seasonalRules", [
								...prefs.seasonalRules,
								{
									id: crypto.randomUUID(),
									name: "",
									startDate: "",
									endDate: "",
									categoryIds: [],
									brandIds: [],
									markup: 1.0,
								},
							])
						}
					>
						<Plus className="h-3 w-3 mr-0.5" />
						{t("common:add")}
					</Button>
				</div>
				{prefs.seasonalRules.length === 0 ? (
					<p className="text-xs text-muted-foreground py-1">
						{t("no_seasonal_rules", {
							defaultValue: "Нет сезонных правил",
						})}
					</p>
				) : (
					<div className="space-y-2">
						{prefs.seasonalRules.map((rule, idx) => (
							<div
								key={rule.id || idx}
								className="border rounded p-2 space-y-1.5"
							>
								<div className="flex items-center gap-1.5">
									<Input
										value={rule.name}
										onChange={(e) =>
											update(
												"seasonalRules",
												prefs.seasonalRules.map((r, i) =>
													i === idx ? { ...r, name: e.target.value } : r,
												),
											)
										}
										placeholder="8 марта"
										className="h-7 text-xs flex-1"
									/>
									<Input
										type="number"
										step="0.01"
										value={rule.markup}
										onChange={(e) =>
											update(
												"seasonalRules",
												prefs.seasonalRules.map((r, i) =>
													i === idx
														? {
																...r,
																markup:
																	Number.parseFloat(e.target.value) || 1.0,
															}
														: r,
												),
											)
										}
										className="h-7 text-xs w-20"
									/>
									<Button
										variant="ghost"
										size="icon"
										className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
										onClick={() =>
											update(
												"seasonalRules",
												prefs.seasonalRules.filter((_, i) => i !== idx),
											)
										}
									>
										<X className="h-3 w-3" />
									</Button>
								</div>
								<div className="flex items-center gap-1.5">
									<Input
										type="date"
										value={rule.startDate}
										onChange={(e) =>
											update(
												"seasonalRules",
												prefs.seasonalRules.map((r, i) =>
													i === idx ? { ...r, startDate: e.target.value } : r,
												),
											)
										}
										className="h-7 text-xs flex-1"
									/>
									<Input
										type="date"
										value={rule.endDate}
										onChange={(e) =>
											update(
												"seasonalRules",
												prefs.seasonalRules.map((r, i) =>
													i === idx ? { ...r, endDate: e.target.value } : r,
												),
											)
										}
										className="h-7 text-xs flex-1"
									/>
								</div>
								<Input
									value={rule.brandIds.join(", ")}
									onChange={(e) =>
										update(
											"seasonalRules",
											prefs.seasonalRules.map((r, i) =>
												i === idx
													? {
															...r,
															brandIds: e.target.value
																.split(/[,\n]/)
																.map((s) => s.trim())
																.filter(Boolean),
														}
													: r,
											),
										)
									}
									placeholder={t("seasonal_brands_placeholder", {
										defaultValue: "Бренды (через запятую)",
									})}
									className="h-7 text-xs"
								/>
								<Input
									value={rule.categoryIds.join(", ")}
									onChange={(e) =>
										update(
											"seasonalRules",
											prefs.seasonalRules.map((r, i) =>
												i === idx
													? {
															...r,
															categoryIds: e.target.value
																.split(/[,\n]/)
																.map((s) => s.trim())
																.filter(Boolean),
														}
													: r,
											),
										)
									}
									placeholder={t("seasonal_categories_placeholder", {
										defaultValue:
											"ID категорий (через запятую) — необязательно",
									})}
									className="h-7 text-xs"
								/>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Logistics notes */}
			<div className="space-y-1.5">
				<Label className="text-xs">{t("logistics_notes")}</Label>
				<Textarea
					value={prefs.logisticsNotes}
					onChange={(e) => update("logisticsNotes", e.target.value)}
					placeholder={t("logistics_placeholder")}
					className="min-h-[60px]"
					rows={2}
				/>
			</div>

			{/* Free text rules for AI */}
			<div className="space-y-1.5">
				<Label className="text-xs">{t("ai_rules")}</Label>
				<Textarea
					value={prefs.freeTextRules}
					onChange={(e) => update("freeTextRules", e.target.value)}
					placeholder={t("ai_rules_placeholder")}
					className="min-h-[80px]"
					rows={3}
				/>
			</div>

			{/* Actions */}
			<div className="flex items-center gap-2 pt-1">
				<Button
					size="sm"
					onClick={() => saveMut.mutate()}
					disabled={saveMut.isPending || !dirty}
					className="h-8"
				>
					{saveMut.isPending ? (
						<>
							<Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
							{t("saving")}
						</>
					) : (
						t("save_settings")
					)}
				</Button>
				<Button
					size="sm"
					variant="ghost"
					className="h-8"
					onClick={() => {
						setExpanded(false);
						if (prefsData) setPrefs(prefsToPrefState(prefsData));
						setDirty(false);
					}}
				>
					{t("collapse")}
				</Button>
				{saveMut.isSuccess && !dirty && (
					<span className="text-xs text-muted-foreground flex items-center gap-1">
						<Check className="h-3 w-3" />
						{t("saved")}
					</span>
				)}
			</div>
		</div>
	);
}

// ── Party Own Catalog (Feature A) ──

function PartyOwnCatalog({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [addOpen, setAddOpen] = useState(false);
	const [newTitle, setNewTitle] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	const {
		data: pages,
		isLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: ["party-own-catalog", partyId, debouncedSearch],
		queryFn: ({ pageParam }) =>
			catalogClient.listProducts({
				ownerPartyId: partyId,
				search: debouncedSearch,
				page: { pageSize: 20, pageToken: pageParam },
				excludeVariants: true,
			}),
		initialPageParam: "",
		getNextPageParam: (last) => last.page?.nextPageToken || undefined,
	});

	const products = pages?.pages.flatMap((p) => p.products) ?? [];

	const createMut = useMutation({
		mutationFn: (title: string) =>
			catalogClient.createProduct({ title, ownerPartyId: partyId }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["party-own-catalog", partyId] });
			toast.success(t("own_catalog_created"));
			setAddOpen(false);
			setNewTitle("");
		},
		onError: () => toast.error(t("own_catalog_create_failed")),
	});

	return (
		<Card className="px-4 py-3 gap-3">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium flex items-center gap-1.5">
					<Package className="h-4 w-4 text-muted-foreground" />
					{t("own_catalog_title")}
				</h3>
				<div className="flex items-center gap-1">
					<Button size="sm" variant="ghost" className="h-7 text-xs" asChild>
						<Link to="/catalog/products" search={{ owner_party_id: partyId }}>
							{t("own_catalog_view_all", {
								defaultValue: "Все товары →",
							})}
						</Link>
					</Button>
					<Button
						size="sm"
						variant="ghost"
						className="h-7 text-xs"
						onClick={() => setAddOpen(true)}
					>
						<Plus className="h-3 w-3 mr-1" />
						{t("own_catalog_add")}
					</Button>
				</div>
			</div>

			<div className="relative">
				<Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
				<Input
					placeholder={t("search_placeholder")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="h-8 pl-8 text-sm"
				/>
			</div>

			{isLoading ? (
				<Skeleton className="h-24 w-full rounded" />
			) : products.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{t("own_catalog_empty")}
				</p>
			) : (
				<div className="border rounded-md overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/50">
								<TableHead className="text-left px-3 py-1.5 font-medium">
									{t("col_title")}
								</TableHead>
								<TableHead className="text-left px-3 py-1.5 font-medium">
									{t("col_vendor_code")}
								</TableHead>
								<TableHead className="text-right px-3 py-1.5 font-medium">
									{t("col_price")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{products.map((p) => {
								const defaultPrice = p.pricing?.find((pr) => pr.isDefault);
								const vendorCode = p.vendorCodes?.[0]?.code ?? "";
								return (
									<TableRow
										key={p.id}
										className="border-b last:border-b-0 hover:bg-muted/30 cursor-pointer"
										onClick={() =>
											navigate({
												to: "/catalog/products/$id",
												params: { id: p.id },
											})
										}
									>
										<TableCell className="px-3 py-1.5">{p.title}</TableCell>
										<TableCell className="px-3 py-1.5 text-muted-foreground">
											{vendorCode}
										</TableCell>
										<TableCell className="px-3 py-1.5 text-right tabular-nums">
											{defaultPrice
												? formatCurrency(
														defaultPrice.valueCents,
														defaultPrice.currency || "RUB",
													)
												: "—"}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
			)}

			{hasNextPage && (
				<Button
					size="sm"
					variant="ghost"
					className="w-full text-xs"
					disabled={isFetchingNextPage}
					onClick={() => fetchNextPage()}
				>
					{isFetchingNextPage ? (
						<Loader2 className="h-3 w-3 animate-spin mr-1" />
					) : null}
					{t("load_more")}
				</Button>
			)}

			{/* Add product dialog */}
			<Dialog open={addOpen} onOpenChange={setAddOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("own_catalog_add_title")}</DialogTitle>
						<DialogDescription>{t("own_catalog_add_desc")}</DialogDescription>
					</DialogHeader>
					<div className="space-y-3 py-2">
						<div className="space-y-1">
							<Label>{t("own_catalog_product_title")}</Label>
							<Input
								value={newTitle}
								onChange={(e) => setNewTitle(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter" && newTitle.trim())
										createMut.mutate(newTitle.trim());
								}}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setAddOpen(false)}>
							{t("cancel", { ns: "common" })}
						</Button>
						<Button
							disabled={!newTitle.trim() || createMut.isPending}
							onClick={() => createMut.mutate(newTitle.trim())}
						>
							{createMut.isPending && (
								<Loader2 className="h-4 w-4 animate-spin mr-1" />
							)}
							{t("create", { ns: "common" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Card>
	);
}

// ── Party Available Products (Feature B) ──

type PartyProductSortKey = "title" | "code" | "price";

function PartyAvailableProducts({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [pickerOpen, setPickerOpen] = useState(false);
	const [sortKey, setSortKey] = useState<PartyProductSortKey | null>(null);
	const [sortDesc, setSortDesc] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	const {
		data: pages,
		isLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: ["party-products", partyId, debouncedSearch],
		queryFn: ({ pageParam }) =>
			partyClient.listPartyProducts({
				partyId,
				search: debouncedSearch,
				page: { pageSize: 20, pageToken: pageParam },
			}),
		initialPageParam: "",
		getNextPageParam: (last) => last.page?.nextPageToken || undefined,
	});

	const items = useMemo(
		() => pages?.pages.flatMap((p) => p.items) ?? [],
		[pages],
	);

	const sortedItems = useMemo(() => {
		if (!sortKey) return items;
		const dir = sortDesc ? -1 : 1;
		return [...items].sort((a, b) => {
			if (sortKey === "price")
				return (
					dir * (Number(a.defaultPriceCents) - Number(b.defaultPriceCents))
				);
			const av =
				sortKey === "title"
					? a.productTitle
					: (a.productVendorCodes?.join(", ") ?? "");
			const bv =
				sortKey === "title"
					? b.productTitle
					: (b.productVendorCodes?.join(", ") ?? "");
			return dir * av.localeCompare(bv, "ru");
		});
	}, [items, sortKey, sortDesc]);

	const toggleSort = (key: PartyProductSortKey) => {
		if (sortKey === key) {
			setSortDesc((prev) => !prev);
		} else {
			setSortKey(key);
			setSortDesc(false);
		}
	};

	const sortIcon = (key: PartyProductSortKey) =>
		sortKey !== key ? (
			<ArrowUpDown className="h-3 w-3 opacity-30" />
		) : sortDesc ? (
			<ArrowDown className="h-3 w-3" />
		) : (
			<ArrowUp className="h-3 w-3" />
		);

	const removeMut = useMutation({
		mutationFn: (productIds: string[]) =>
			partyClient.removePartyProducts({ partyId, productIds }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["party-products", partyId] });
			setSelectedIds(new Set());
			toast.success(t("available_products_removed"));
		},
		onError: () => toast.error(t("available_products_remove_failed")),
	});

	const toggleAvailability = useMutation({
		mutationFn: ({
			productId,
			isAvailable,
		}: { productId: string; isAvailable: boolean }) =>
			partyClient.setPartyProducts({
				partyId,
				entries: [{ productId, isAvailable }],
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["party-products", partyId] });
		},
	});

	const updateCustomPrice = useMutation({
		mutationFn: ({
			productId,
			customPriceCents,
		}: { productId: string; customPriceCents: bigint | undefined }) =>
			partyClient.setPartyProducts({
				partyId,
				entries: [{ productId, customPriceCents, isAvailable: true }],
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["party-products", partyId] });
		},
	});

	const toggleSelect = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	return (
		<Card className="px-4 py-3 gap-3">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium flex items-center gap-1.5">
					<ShoppingCart className="h-4 w-4 text-muted-foreground" />
					{t("available_products_title")}
				</h3>
				<div className="flex items-center gap-1">
					{selectedIds.size > 0 && (
						<Button
							size="sm"
							variant="destructive"
							className="h-7 text-xs"
							disabled={removeMut.isPending}
							onClick={() => removeMut.mutate(Array.from(selectedIds))}
						>
							<Trash2 className="h-3 w-3 mr-1" />
							{t("available_products_remove")} ({selectedIds.size})
						</Button>
					)}
					<Button
						size="sm"
						variant="ghost"
						className="h-7 text-xs"
						onClick={() => setPickerOpen(true)}
					>
						<Plus className="h-3 w-3 mr-1" />
						{t("available_products_add")}
					</Button>
				</div>
			</div>

			<div className="relative">
				<Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
				<Input
					placeholder={t("search_placeholder")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="h-8 pl-8 text-sm"
				/>
			</div>

			{isLoading ? (
				<Skeleton className="h-24 w-full rounded" />
			) : items.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{t("available_products_empty")}
				</p>
			) : (
				<div className="border rounded-md overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/50">
								<TableHead className="w-8 px-2 py-1.5" />
								<TableHead className="text-left px-3 py-1.5 font-medium">
									<button
										type="button"
										className="inline-flex items-center gap-1 hover:text-foreground cursor-pointer"
										onClick={() => toggleSort("title")}
									>
										{t("col_title")}
										{sortIcon("title")}
									</button>
								</TableHead>
								<TableHead className="text-left px-3 py-1.5 font-medium">
									<button
										type="button"
										className="inline-flex items-center gap-1 hover:text-foreground cursor-pointer"
										onClick={() => toggleSort("code")}
									>
										{t("col_vendor_code")}
										{sortIcon("code")}
									</button>
								</TableHead>
								<TableHead className="text-right px-3 py-1.5 font-medium">
									<button
										type="button"
										className="inline-flex items-center gap-1 hover:text-foreground cursor-pointer"
										onClick={() => toggleSort("price")}
									>
										{t("col_default_price")}
										{sortIcon("price")}
									</button>
								</TableHead>
								<TableHead className="text-right px-3 py-1.5 font-medium">
									{t("col_custom_price")}
								</TableHead>
								<TableHead className="text-center px-3 py-1.5 font-medium">
									{t("col_available")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{sortedItems.map((item) => (
								<TableRow
									key={item.id}
									className="border-b last:border-b-0 hover:bg-muted/30"
								>
									<TableCell className="px-2 py-1.5 text-center">
										<Checkbox
											checked={selectedIds.has(item.productId)}
											onCheckedChange={() => toggleSelect(item.productId)}
										/>
									</TableCell>
									<TableCell className="px-3 py-1.5">
										{item.productTitle}
									</TableCell>
									<TableCell className="px-3 py-1.5 text-muted-foreground">
										{item.productVendorCodes?.join(", ") || ""}
									</TableCell>
									<TableCell className="px-3 py-1.5 text-right tabular-nums">
										{Number(item.defaultPriceCents) > 0
											? formatCurrency(
													item.defaultPriceCents,
													item.defaultPriceCurrency || undefined,
												)
											: "—"}
									</TableCell>
									<TableCell className="px-3 py-1.5 text-right">
										<InlineCustomPrice
											value={item.customPriceCents}
											currency={item.customCurrency || undefined}
											onSave={(cents) =>
												updateCustomPrice.mutate({
													productId: item.productId,
													customPriceCents: cents,
												})
											}
										/>
									</TableCell>
									<TableCell className="px-3 py-1.5 text-center">
										<Switch
											checked={item.isAvailable}
											onCheckedChange={(checked) =>
												toggleAvailability.mutate({
													productId: item.productId,
													isAvailable: checked,
												})
											}
										/>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}

			{hasNextPage && (
				<Button
					size="sm"
					variant="ghost"
					className="w-full text-xs"
					disabled={isFetchingNextPage}
					onClick={() => fetchNextPage()}
				>
					{isFetchingNextPage ? (
						<Loader2 className="h-3 w-3 animate-spin mr-1" />
					) : null}
					{t("load_more")}
				</Button>
			)}

			{/* Product picker dialog */}
			<ProductPickerDialog
				open={pickerOpen}
				onOpenChange={setPickerOpen}
				partyId={partyId}
				existingProductIds={new Set(items.map((i) => i.productId))}
			/>
		</Card>
	);
}

// Inline editable custom price cell
function InlineCustomPrice({
	value,
	currency,
	onSave,
}: {
	value?: bigint;
	currency?: string;
	onSave: (cents: bigint | undefined) => void;
}) {
	const { t } = useTranslation("parties");
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	const startEdit = () => {
		setDraft(
			value != null && Number(value) > 0
				? (Number(value) / 100).toString()
				: "",
		);
		setEditing(true);
		setTimeout(() => inputRef.current?.focus(), 0);
	};

	const commit = () => {
		setEditing(false);
		const num = Number.parseFloat(draft);
		if (draft.trim() === "" || Number.isNaN(num)) {
			onSave(undefined);
		} else {
			onSave(BigInt(Math.round(num * 100)));
		}
	};

	if (editing) {
		return (
			<Input
				ref={inputRef}
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === "Enter") commit();
					if (e.key === "Escape") setEditing(false);
				}}
				className="h-6 w-24 text-right text-sm px-1 ml-auto"
			/>
		);
	}

	return (
		<Button
			variant="ghost"
			onClick={startEdit}
			className="tabular-nums text-right w-full h-auto p-0 hover:underline cursor-pointer justify-end"
		>
			{value != null && Number(value) > 0
				? formatCurrency(value, currency || undefined)
				: t("custom_price_placeholder")}
		</Button>
	);
}

// ── Party Quotes tab ──

function PartyQuotesTab({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const navigate = useNavigate();

	const { data, isLoading } = useQuery({
		queryKey: ["party-quotes", partyId],
		queryFn: () =>
			quoteClient.listQuotes({
				customerPartyId: partyId,
				page: { pageSize: 100, pageToken: "" },
			}),
	});
	const quotes = data?.quotes ?? [];

	const quoteTotalCents = (quote: (typeof quotes)[number]) =>
		(quote.lines ?? []).reduce(
			(sum, line) => sum + line.quantity * Number(line.unitPriceCents),
			0,
		);

	return (
		<Card className="px-4 py-3 gap-3">
			<h3 className="text-sm font-medium flex items-center gap-1.5">
				<FileText className="h-4 w-4 text-muted-foreground" />
				{t("quotes_title")}
			</h3>
			{isLoading ? (
				<Skeleton className="h-24 w-full rounded" />
			) : quotes.length === 0 ? (
				<p className="text-sm text-muted-foreground py-4 text-center">
					{t("quotes_empty")}
				</p>
			) : (
				<div className="border rounded-md overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="border-b bg-muted/50">
								<TableHead className="text-left px-3 py-1.5 font-medium">
									{t("col_quote_title")}
								</TableHead>
								<TableHead className="text-left px-3 py-1.5 font-medium">
									{t("col_quote_status")}
								</TableHead>
								<TableHead className="text-right px-3 py-1.5 font-medium">
									{t("col_quote_total")}
								</TableHead>
								<TableHead className="text-right px-3 py-1.5 font-medium">
									{t("col_quote_created")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{quotes.map((quote) => {
								const totalCents = quoteTotalCents(quote);
								return (
									<TableRow
										key={quote.id}
										className="border-b last:border-b-0 hover:bg-muted/30 cursor-pointer"
										onClick={() =>
											navigate({
												to: "/commerce/quotes/$id",
												params: { id: quote.id },
											})
										}
									>
										<TableCell className="px-3 py-1.5">
											{quote.title || `КП-${quote.id.slice(0, 8)}`}
										</TableCell>
										<TableCell className="px-3 py-1.5">
											<CommerceStatusBadge
												entity="quote"
												status={quote.status}
											/>
										</TableCell>
										<TableCell className="px-3 py-1.5 text-right tabular-nums">
											{totalCents > 0
												? formatCurrency(totalCents, quote.currency || "RUB")
												: "—"}
										</TableCell>
										<TableCell className="px-3 py-1.5 text-right text-muted-foreground">
											{quote.createdAt
												? formatDate(Number(quote.createdAt.seconds) * 1000)
												: "—"}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
			)}
		</Card>
	);
}

// Product picker dialog for adding products to party
function ProductPickerDialog({
	open,
	onOpenChange,
	partyId,
	existingProductIds,
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	partyId: string;
	existingProductIds: Set<string>;
}) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selected, setSelected] = useState<Set<string>>(new Set());

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		if (open) {
			setSelected(new Set());
			setSearch("");
			setDebouncedSearch("");
		}
	}, [open]);

	const { data: productsData, isLoading } = useQuery({
		queryKey: ["product-picker", debouncedSearch],
		queryFn: () =>
			catalogClient.listProducts({
				search: debouncedSearch,
				page: { pageSize: 50, pageToken: "" },
				excludeVariants: true,
			}),
		enabled: open,
	});

	const products = (productsData?.products ?? []).filter(
		(p) => !existingProductIds.has(p.id),
	);

	const toggleProduct = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const saveMut = useMutation({
		mutationFn: () =>
			partyClient.setPartyProducts({
				partyId,
				entries: Array.from(selected).map((productId) => ({
					productId,
					isAvailable: true,
				})),
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["party-products", partyId] });
			toast.success(t("available_products_saved"));
			onOpenChange(false);
		},
		onError: () => toast.error(t("available_products_save_failed")),
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>{t("available_products_picker_title")}</DialogTitle>
					<DialogDescription>
						{t("available_products_picker_desc")}
					</DialogDescription>
				</DialogHeader>
				<div className="relative">
					<Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="h-8 pl-8 text-sm"
					/>
				</div>
				<div className="max-h-72 overflow-y-auto border rounded-md">
					{isLoading ? (
						<Skeleton className="h-24 w-full rounded" />
					) : products.length === 0 ? (
						<p className="text-sm text-muted-foreground py-6 text-center">
							{t("nothing_found")}
						</p>
					) : (
						<div className="divide-y">
							{products.map((p) => (
								<label
									key={p.id}
									className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer"
								>
									<Checkbox
										checked={selected.has(p.id)}
										onCheckedChange={() => toggleProduct(p.id)}
									/>
									<div className="flex-1 min-w-0">
										<p className="text-sm truncate">{p.title}</p>
										{p.vendorCodes?.[0] && (
											<p className="text-xs text-muted-foreground">
												{p.vendorCodes[0].code}
											</p>
										)}
									</div>
								</label>
							))}
						</div>
					)}
				</div>
				<DialogFooter>
					<Button variant="ghost" onClick={() => onOpenChange(false)}>
						{t("cancel", { ns: "common" })}
					</Button>
					<Button
						disabled={selected.size === 0 || saveMut.isPending}
						onClick={() => saveMut.mutate()}
					>
						{saveMut.isPending && (
							<Loader2 className="h-4 w-4 animate-spin mr-1" />
						)}
						{t("add", { ns: "common" })} ({selected.size})
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ── Customer Order Stats ──

function CustomerStatsCard({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const formatDate = new Intl.DateTimeFormat(undefined, {
		dateStyle: "medium",
	});
	const { data } = useQuery({
		queryKey: ["customer-order-stats", partyId],
		queryFn: () => partyClient.getCustomerOrderStats({ partyId }),
	});

	if (!data || data.totalOrders === 0) return null;

	return (
		<Card className="px-4 py-3 gap-2">
			<h3 className="text-sm font-medium">{t("stats_title")}</h3>
			<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
				<span className="text-muted-foreground">{t("stats_orders")}</span>
				<span className="text-right font-medium tabular-nums">
					{data.totalOrders}
				</span>
				<span className="text-muted-foreground">{t("stats_total")}</span>
				<span className="text-right font-medium tabular-nums">
					{formatCurrency(data.totalSpentCents)}
				</span>
				<span className="text-muted-foreground">{t("stats_avg")}</span>
				<span className="text-right font-medium tabular-nums">
					{formatCurrency(data.avgOrderValueCents)}
				</span>
				{data.lastOrderAt && (
					<>
						<span className="text-muted-foreground">
							{t("stats_last_order")}
						</span>
						<span className="text-right tabular-nums">
							{formatDate.format(
								new Date(Number(data.lastOrderAt.seconds) * 1000),
							)}
						</span>
					</>
				)}
				{Number(data.creditLimitCents) > 0 && (
					<>
						<span className="text-muted-foreground">
							{t("stats_credit_limit")}
						</span>
						<span className="text-right font-medium tabular-nums">
							{formatCurrency(data.creditLimitCents)}
						</span>
					</>
				)}
			</div>
		</Card>
	);
}

// ── Generate Client Export ──

function GenerateClientExportCard({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [templateId, setTemplateId] = useState<string>("");
	const [running, setRunning] = useState(false);

	const { data: tplData } = useQuery({
		queryKey: ["party-export-templates-generate", partyId],
		queryFn: () =>
			exportClient.listExportTemplates({
				partyId,
				page: { pageSize: 50, pageToken: "" },
			}),
	});
	const templates = tplData?.templates ?? [];

	const createMut = useMutation({
		mutationFn: async () => {
			const { columns: defaultCols } =
				await exportClient.getDefaultExportColumns({});
			const colsJson = defaultCols.map((c) => ({
				field: c.field,
				header: c.header,
				width: c.width,
				...(c.numberFormat ? { number_format: c.numberFormat } : {}),
			}));
			return exportClient.createExportTemplate({
				name: t("export_for_client", { num: templates.length + 1 }),
				scope: "party",
				partyId,
				format: "xlsx",
				entityType: "client_catalog",
				isDefault: templates.length === 0,
				columns: Struct.fromJson({
					value: colsJson,
				} as unknown as Parameters<typeof Struct.fromJson>[0]),
				headerConfig: Struct.fromJson({
					show_date: true,
					show_customer: true,
				} as unknown as Parameters<typeof Struct.fromJson>[0]),
				footerConfig: Struct.fromJson({
					show_totals: true,
				} as unknown as Parameters<typeof Struct.fromJson>[0]),
				styling: Struct.fromJson({
					alternate_rows: true,
				} as unknown as Parameters<typeof Struct.fromJson>[0]),
			});
		},
		onSuccess: (res) => {
			qc.invalidateQueries({
				queryKey: ["party-export-templates-generate", partyId],
			});
			qc.invalidateQueries({
				queryKey: ["party-export-templates", partyId],
			});
			navigate({
				to: `/settings/export-templates/${res.id}?partyId=${partyId}`,
			});
		},
	});

	useEffect(() => {
		if (!templateId && templates.length > 0) {
			const def = templates.find((tt) => tt.isDefault);
			setTemplateId(def?.id ?? templates[0].id);
		}
	}, [templates, templateId]);

	const generate = async () => {
		if (running) return;
		setRunning(true);
		try {
			const res = await exportClient.exportClientCatalog({
				partyId,
				templateId,
			});
			if (res.fileUrl) {
				const a = document.createElement("a");
				a.href = res.fileUrl;
				a.download = res.fileName || "export";
				a.target = "_blank";
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				toast.success(
					t("generate_export_success", {
						count: res.productCount,
						defaultValue: "Экспорт готов: {{count}} товаров",
					}),
				);
			}
		} catch (e: any) {
			toast.error(e?.message ?? t("generate_export_error"));
		} finally {
			setRunning(false);
		}
	};

	return (
		<Card className="px-4 py-3 gap-3">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium flex items-center gap-1.5">
					<FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
					{t("generate_export_title", {
						defaultValue: "Сгенерировать экспорт",
					})}
				</h3>
				<Button
					size="sm"
					variant="ghost"
					className="h-7 text-xs"
					onClick={() => createMut.mutate()}
					disabled={createMut.isPending}
				>
					<Plus className="h-3 w-3 mr-1" />
					{t("create_custom_template", {
						defaultValue: "Создать шаблон",
					})}
				</Button>
			</div>
			<div className="flex items-end gap-2">
				<div className="flex-1 min-w-0 space-y-1">
					<Label className="text-xs text-muted-foreground">
						{t("generate_export_template", { defaultValue: "Шаблон" })}
					</Label>
					<Select value={templateId || "_none"} onValueChange={setTemplateId}>
						<SelectTrigger className="h-9 text-sm">
							<SelectValue
								placeholder={t("no_templates", {
									defaultValue: "Нет шаблонов",
								})}
							/>
						</SelectTrigger>
						<SelectContent>
							{templates.length === 0 ? (
								<SelectItem value="_none" disabled>
									{t("no_templates", { defaultValue: "Нет шаблонов" })}
								</SelectItem>
							) : (
								templates.map((tpl) => (
									<SelectItem key={tpl.id} value={tpl.id}>
										{tpl.name}
									</SelectItem>
								))
							)}
						</SelectContent>
					</Select>
				</div>
				<Button
					className="h-9"
					onClick={generate}
					disabled={running || !templateId || templates.length === 0}
				>
					{running ? (
						<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
					) : (
						<FileText className="h-3.5 w-3.5 mr-1.5" />
					)}
					{t("generate_export_action", { defaultValue: "Сгенерировать" })}
				</Button>
			</div>
		</Card>
	);
}

// ── Customer Margin Rule ──

const MARGIN_SOURCE_KEYS: Record<string, string> = {
	custom: "margin_source_custom",
	margin: "margin_source_margin",
	currency_mismatch: "margin_source_currency_mismatch",
	no_supplier: "margin_source_no_supplier",
};

type MarginOverrideRow = { rowId: number; categoryId: string; pct: string };

function CustomerMarginCard({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const rowIdRef = useRef(0);
	const [defaultPct, setDefaultPct] = useState("");
	const [currency, setCurrency] = useState("");
	const [overrides, setOverrides] = useState<MarginOverrideRow[]>([]);
	const [preview, setPreview] = useState<PricedProduct[] | null>(null);

	const { data: rule } = useQuery({
		queryKey: ["customer-pricing-rule", partyId],
		queryFn: () =>
			catalogClient.getCustomerPricingRule({ customerPartyId: partyId }),
		retry: false,
	});

	const { data: categoriesResp } = useQuery({
		queryKey: ["categories", "*"],
		queryFn: () => catalogClient.listCategories({ parentId: "*" }),
	});
	const categories = categoriesResp?.categories ?? [];

	useEffect(() => {
		if (!rule) return;
		setDefaultPct(rule.defaultMarginPct ? String(rule.defaultMarginPct) : "");
		setCurrency(rule.currency ?? "");
		setOverrides(
			Object.entries(rule.categoryOverrides ?? {}).map(([categoryId, pct]) => ({
				rowId: rowIdRef.current++,
				categoryId,
				pct: String(pct),
			})),
		);
	}, [rule]);

	const parsePct = (v: string) => Number.parseFloat(v.replace(",", "."));

	const saveMut = useMutation({
		mutationFn: () => {
			const categoryOverrides: Record<string, number> = {};
			for (const o of overrides) {
				const pct = parsePct(o.pct);
				if (o.categoryId && Number.isFinite(pct)) {
					categoryOverrides[o.categoryId] = pct;
				}
			}
			const pct = parsePct(defaultPct);
			return catalogClient.setCustomerPricingRule({
				rule: {
					customerPartyId: partyId,
					defaultMarginPct: Number.isFinite(pct) ? pct : 0,
					categoryOverrides,
					currency,
				},
			});
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["customer-pricing-rule", partyId] });
			toast.success(t("margin_saved"));
		},
		onError: () => toast.error(t("margin_save_failed")),
	});

	const previewMut = useMutation({
		mutationFn: () =>
			catalogClient.previewCustomerPricing({
				customerPartyId: partyId,
				productIds: [],
				limit: 20,
			}),
		onSuccess: (res) => setPreview(res.items),
		onError: () => toast.error(t("margin_preview_failed")),
	});

	const categoryLabel = (id: string) => {
		const cat = categories.find((c) => c.id === id);
		return cat ? cat.path || cat.name : id;
	};

	const usedCategoryIds = new Set(overrides.map((o) => o.categoryId));

	const updateOverride = (rowId: number, patch: Partial<MarginOverrideRow>) =>
		setOverrides((prev) =>
			prev.map((o) => (o.rowId === rowId ? { ...o, ...patch } : o)),
		);

	return (
		<Card className="px-4 py-3 gap-3">
			<h3 className="text-sm font-medium flex items-center gap-1.5">
				<Percent className="h-4 w-4 text-muted-foreground" />
				{t("margin_title")}
			</h3>
			<div className="flex items-end gap-2 flex-wrap">
				<div className="space-y-1">
					<Label className="text-xs text-muted-foreground">
						{t("margin_default_pct")}
					</Label>
					<Input
						type="number"
						step="0.1"
						className="h-9 w-32 text-sm"
						value={defaultPct}
						onChange={(e) => setDefaultPct(e.target.value)}
						placeholder="0"
					/>
				</div>
				<div className="space-y-1">
					<Label className="text-xs text-muted-foreground">
						{t("margin_currency")}
					</Label>
					<Select
						value={currency || "_supplier"}
						onValueChange={(v) => setCurrency(v === "_supplier" ? "" : v)}
					>
						<SelectTrigger className="h-9 w-52 text-sm">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="_supplier">
								{t("margin_currency_supplier")}
							</SelectItem>
							{CURRENCIES.map((c) => (
								<SelectItem key={c.code} value={c.code}>
									{c.code} ({c.symbol})
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
			<div className="space-y-2">
				<div className="flex items-center justify-between">
					<Label className="text-xs text-muted-foreground">
						{t("margin_overrides")}
					</Label>
					<Button
						size="sm"
						variant="ghost"
						className="h-7 text-xs"
						onClick={() =>
							setOverrides((prev) => [
								...prev,
								{ rowId: rowIdRef.current++, categoryId: "", pct: "" },
							])
						}
					>
						<Plus className="h-3 w-3 mr-1" />
						{t("margin_add_override")}
					</Button>
				</div>
				{overrides.map((o) => (
					<div key={o.rowId} className="flex items-center gap-2">
						<Select
							value={o.categoryId}
							onValueChange={(v) => updateOverride(o.rowId, { categoryId: v })}
						>
							<SelectTrigger className="h-9 flex-1 min-w-0 text-sm">
								<SelectValue placeholder={t("margin_override_category")} />
							</SelectTrigger>
							<SelectContent>
								{categories
									.filter(
										(c) => c.id === o.categoryId || !usedCategoryIds.has(c.id),
									)
									.map((c) => (
										<SelectItem key={c.id} value={c.id}>
											{c.path || c.name}
										</SelectItem>
									))}
								{o.categoryId &&
									!categories.some((c) => c.id === o.categoryId) && (
										<SelectItem value={o.categoryId}>
											{categoryLabel(o.categoryId)}
										</SelectItem>
									)}
							</SelectContent>
						</Select>
						<Input
							type="number"
							step="0.1"
							className="h-9 w-24 text-sm"
							value={o.pct}
							onChange={(e) => updateOverride(o.rowId, { pct: e.target.value })}
							placeholder="%"
						/>
						<Button
							size="sm"
							variant="ghost"
							className="h-9 w-9 p-0 text-muted-foreground"
							onClick={() =>
								setOverrides((prev) =>
									prev.filter((row) => row.rowId !== o.rowId),
								)
							}
						>
							<X className="h-3.5 w-3.5" />
						</Button>
					</div>
				))}
			</div>
			<div className="flex items-center gap-2">
				<Button
					size="sm"
					onClick={() => saveMut.mutate()}
					disabled={saveMut.isPending}
				>
					{saveMut.isPending ? (
						<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
					) : (
						<Check className="h-3.5 w-3.5 mr-1.5" />
					)}
					{t("margin_save")}
				</Button>
				<Button
					size="sm"
					variant="outline"
					onClick={() => previewMut.mutate()}
					disabled={previewMut.isPending}
				>
					{previewMut.isPending ? (
						<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
					) : (
						<Search className="h-3.5 w-3.5 mr-1.5" />
					)}
					{t("margin_preview")}
				</Button>
			</div>
			{preview !== null &&
				(preview.length === 0 ? (
					<p className="text-xs text-muted-foreground">
						{t("margin_preview_empty")}
					</p>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="text-xs">
									{t("margin_col_title")}
								</TableHead>
								<TableHead className="text-xs">
									{t("margin_col_cost")}
								</TableHead>
								<TableHead className="text-xs">{t("margin_col_pct")}</TableHead>
								<TableHead className="text-xs">
									{t("margin_col_price")}
								</TableHead>
								<TableHead className="text-xs">
									{t("margin_col_source")}
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{preview.map((p) => (
								<TableRow key={p.productId}>
									<TableCell className="text-xs max-w-[240px] truncate">
										{p.title}
									</TableCell>
									<TableCell className="text-xs whitespace-nowrap">
										{Number(p.costCents) > 0
											? formatCurrency(p.costCents, p.costCurrency || "RUB")
											: "—"}
									</TableCell>
									<TableCell className="text-xs whitespace-nowrap">
										{p.source === "margin" ? `${p.marginPct}%` : "—"}
									</TableCell>
									<TableCell className="text-xs whitespace-nowrap">
										{Number(p.priceCents) > 0
											? formatCurrency(p.priceCents, p.currency || "RUB")
											: "—"}
									</TableCell>
									<TableCell>
										<Badge
											variant={
												p.source === "no_supplier" ||
												p.source === "currency_mismatch"
													? "destructive"
													: "secondary"
											}
											className="text-[10px]"
										>
											{t(MARGIN_SOURCE_KEYS[p.source] ?? p.source)}
										</Badge>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				))}
		</Card>
	);
}

// ── Supplier Feeds ──

function SupplierFeedsCard({ partyId }: { partyId: string }) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const [editing, setEditing] = useState<string | null>(null);
	const [draftName, setDraftName] = useState("");
	const [draftUrl, setDraftUrl] = useState("");
	const [draftType, setDraftType] = useState("yandex_market");

	const { data: feedsData } = useQuery({
		queryKey: ["party-supplier-feeds", partyId],
		queryFn: () => partyClient.listPartySupplierFeeds({ partyId }),
	});
	const feeds = feedsData?.feeds ?? [];

	const createMut = useMutation({
		mutationFn: () =>
			partyClient.createPartySupplierFeed({
				partyId,
				name: draftName || "Main",
				feedUrl: draftUrl,
				feedType: draftType,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["party-supplier-feeds", partyId] });
			setEditing(null);
			setDraftName("");
			setDraftUrl("");
			setDraftType("yandex_market");
			toast.success(t("feed_created", { defaultValue: "Фид добавлен" }));
		},
		onError: () =>
			toast.error(
				t("feed_save_failed", { defaultValue: "Не удалось сохранить" }),
			),
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => partyClient.deletePartySupplierFeed({ id }),
		onSuccess: () =>
			qc.invalidateQueries({ queryKey: ["party-supplier-feeds", partyId] }),
	});

	return (
		<Card className="px-4 py-3 gap-3">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium flex items-center gap-1.5">
					<Package className="h-4 w-4 text-muted-foreground" />
					{t("supplier_feeds_title", { defaultValue: "Фиды поставщика" })}
				</h3>
				<Button
					size="sm"
					variant="ghost"
					className="h-7 text-xs"
					onClick={() => setEditing("new")}
				>
					<Plus className="h-3 w-3 mr-1" />
					{t("common:add")}
				</Button>
			</div>
			{feeds.length === 0 && editing !== "new" ? (
				<p className="text-xs text-muted-foreground">
					{t("supplier_feeds_empty", {
						defaultValue: "Фиды не настроены",
					})}
				</p>
			) : (
				<div className="space-y-2">
					{feeds.map((feed) => (
						<div
							key={feed.id}
							className="flex items-center gap-2 border rounded p-2"
						>
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-1.5">
									<span className="text-sm font-medium truncate">
										{feed.name || feed.feedUrl || "—"}
									</span>
									<Badge variant="outline" className="text-[10px] shrink-0">
										{feed.feedType}
									</Badge>
								</div>
								<p className="text-xs text-muted-foreground truncate">
									{feed.feedUrl}
								</p>
								{feed.lastImportAt && (
									<p className="text-[10px] text-muted-foreground">
										{t("feed_last_import", {
											defaultValue: "Последний импорт",
										})}
										:{" "}
										{new Date(
											Number(feed.lastImportAt.seconds) * 1000,
										).toLocaleDateString()}
										{feed.lastStatus ? ` · ${feed.lastStatus}` : ""}
										{feed.itemCount > 0 ? ` · ${feed.itemCount}` : ""}
									</p>
								)}
							</div>
							<Button
								variant="ghost"
								size="icon"
								className="h-7 w-7 text-muted-foreground hover:text-destructive"
								onClick={() => deleteMut.mutate(feed.id)}
							>
								<Trash2 className="h-3 w-3" />
							</Button>
						</div>
					))}
					{editing === "new" && (
						<div className="border rounded p-2 space-y-1.5">
							<Input
								value={draftName}
								onChange={(e) => setDraftName(e.target.value)}
								placeholder={t("feed_name_placeholder", {
									defaultValue: "Название (Main, Сезонный...)",
								})}
								className="h-7 text-xs"
							/>
							<Input
								value={draftUrl}
								onChange={(e) => setDraftUrl(e.target.value)}
								placeholder="https://supplier.example.com/feed.xml"
								className="h-7 text-xs"
							/>
							<div className="flex items-center gap-1.5">
								<Select value={draftType} onValueChange={setDraftType}>
									<SelectTrigger className="h-7 text-xs flex-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="yandex_market">Yandex Market</SelectItem>
										<SelectItem value="csv">CSV</SelectItem>
										<SelectItem value="xlsx">XLSX</SelectItem>
										<SelectItem value="custom">Custom</SelectItem>
									</SelectContent>
								</Select>
								<Button
									size="sm"
									className="h-7 text-xs"
									disabled={!draftUrl || createMut.isPending}
									onClick={() => createMut.mutate()}
								>
									{createMut.isPending ? (
										<Loader2 className="h-3 w-3 animate-spin" />
									) : (
										t("common:save")
									)}
								</Button>
								<Button
									size="sm"
									variant="ghost"
									className="h-7 text-xs"
									onClick={() => setEditing(null)}
								>
									{t("common:cancel")}
								</Button>
							</div>
						</div>
					)}
				</div>
			)}
		</Card>
	);
}

// ── Sales Rep ──

function SalesRepCard({
	partyId,
	currentSalesRepUserId,
	onChanged,
}: {
	partyId: string;
	currentSalesRepUserId: string;
	onChanged: () => void;
}) {
	const { t } = useTranslation("parties");
	const qc = useQueryClient();
	const [editing, setEditing] = useState(false);

	const { data: membersData } = useQuery({
		queryKey: ["workspace-members"],
		queryFn: () => memberClient.listMembers({ search: "" }),
	});
	const members = membersData?.members ?? [];

	const updateMut = useMutation({
		mutationFn: (userId: string) =>
			partyClient.updateParty({
				id: partyId,
				salesRepUserId: userId,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["party", partyId] });
			onChanged();
			setEditing(false);
			toast.success(t("toast_saved"));
		},
		onError: () => toast.error(t("save_failed")),
	});

	const current = members.find((m) => m.userId === currentSalesRepUserId);

	return (
		<Card className="px-4 py-3 gap-3">
			<div className="flex items-center justify-between">
				<h3 className="text-sm font-medium flex items-center gap-1.5">
					<User className="h-4 w-4 text-muted-foreground" />
					{t("sales_rep_title", { defaultValue: "Менеджер" })}
				</h3>
				{!editing && (
					<Button
						size="sm"
						variant="ghost"
						className="h-7 text-xs"
						onClick={() => setEditing(true)}
					>
						<Edit2 className="h-3 w-3 mr-1" />
						{t("common:edit")}
					</Button>
				)}
			</div>
			{editing ? (
				<div className="flex items-center gap-2">
					<Select
						value={currentSalesRepUserId || "_none"}
						onValueChange={(v) => updateMut.mutate(v === "_none" ? "" : v)}
					>
						<SelectTrigger className="h-8 text-sm flex-1">
							<SelectValue
								placeholder={t("sales_rep_pick", {
									defaultValue: "Выберите менеджера",
								})}
							/>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="_none">
								{t("sales_rep_none", { defaultValue: "Не назначен" })}
							</SelectItem>
							{members.map((m) => (
								<SelectItem key={m.userId} value={m.userId}>
									{m.name || m.email}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Button
						size="sm"
						variant="ghost"
						className="h-8"
						onClick={() => setEditing(false)}
					>
						{t("common:cancel")}
					</Button>
				</div>
			) : current ? (
				<div className="flex items-center gap-2 text-sm">
					<span className="font-medium">{current.name || current.email}</span>
					{current.email && current.name && (
						<span className="text-xs text-muted-foreground">
							{current.email}
						</span>
					)}
				</div>
			) : (
				<p className="text-xs text-muted-foreground">
					{t("sales_rep_none", { defaultValue: "Не назначен" })}
				</p>
			)}
		</Card>
	);
}
